import styled from '@emotion/styled'
import { CircularProgress, FormControlLabel, Radio, RadioGroup, Skeleton, TextField } from '@mui/material'
import { SkeletonCustom } from './FigmaText'
import { Elements } from '@stripe/react-stripe-js'
import * as stripeJs from '@stripe/stripe-js'
import { createElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import * as Yup from 'yup'
import {
  getPhoneForSwish,
  getPhoneForVippsMobile,
  isValidNumber,
  getCountryCodeFromClient
} from '../../../pure-js/libs/PhoneHelper'
import {
  getToBePaidData,
  getUserPaymentStatus,
  hasPendingPricedActivities
} from '../../../pure-js/libs/EnrollmentHelper'
import {
  GROUP_PAY_FOR_BOTH_PAYMENT_METHODS,
  getGroupToBePaidData,
  resolveEnrollmentPaymentView,
  resolveGroupPaymentEnrollmentId
} from '../../../pure-js/libs/EnrollmentPaymentViewHelper'
import {
  calcFritidskortetAmounts,
  getPaymentMethodFee,
  resolveAdministrationFeeAmount
} from '../../../pure-js/libs/PaymentHelper'
import { getSwishErrorTextKey, isKnownSwishErrorCode } from '../../../pure-js/libs/SwishErrorMessages'
import { formatToBePaid as formatToBePaidHelper } from '../utils/formatToBePaid'
import { withFritidskortetPollLock } from '../utils/fritidskortetPollLock'
import { formatGrooverText, getGrooverText, getGrooverTextStyle } from '../libs/GrooverTextRepository'
import GrooverText from './GrooverText'
import toast from 'react-hot-toast'
import {
  Activity,
  Currency,
  EnrollmentPaymentStatus,
  EnrollmentPublic,
  EnrollmentWithPayment,
  PayFor,
  PayForEnum,
  PaymentMethod,
  PaymentMethodFee
} from '../../../pure-js/types/GrooverTypes'
import {
  CreateStripePaymentIntentWebRequest,
  StripeConfig,
  StripePaymentIntentStatusValue
} from '../../../pure-js/types/StripeTypes'
import { FritidskortetSubmitPaymentDetailsRequest } from '../../../pure-js/types/FritidskortetTypes'
import { EpassiInitiatePaymentResponse } from '../../../pure-js/types/EpassiTypes'
import { CreatePaymentInput, SwishPaymentStatus } from '../../../pure-js/types/SwishTypes'
import { BorderRadixes } from '../enums/BorderRadixes'
import {
  EIGHT_PX,
  FOUR_PX,
  SIX_PX,
  SIXTEEN_PX,
  THIRTY_TWO_PX,
  TWELVE_PX,
  TWENTY_FOUR_PX,
  TWO_PX
} from '../enums/Spacings'
import { State } from '../hooks/useAppState'
import { useClient } from '../hooks/useClient'
import useDiscountCalculations from '../hooks/useDiscountCalculations'
import { useForm } from '../hooks/useForm'
import { getLanguageCode } from '../hooks/useDefaultLanguage'
import {
  cancelSwishPaymentIntent,
  createStripePaymentIntent,
  createSwishPaymentIntent,
  createVippsPayment,
  cancelVippsPayment,
  getCreditForUser,
  payWithUserCredit,
  payForGroupWithUserCredit,
  createVippsSessionCheckout,
  getObfuscatedEnrollment,
  payForGroup,
  payForGroupWithEpassi,
  payForGroupWithSwish,
  payForGroupWithVipps,
  fritidskortetSubmitPaymentDetails,
  fritidskortetGetPaymentStatus,
  fritidskortetConfirmPayout,
  epassiInitiatePayment
} from '../libs/CloudFunctionsApiHandler'
import {
  getStripeConfig,
  useEnrollmentPublic,
  useStripePaymentIntentStatus,
  useSwishPaymentIntentStatus,
  useVippsPaymentStatus
} from '../libs/DBApiHandler'
import getStripe from '../libs/getStripe'
import { isGetObfuscatedEnrollmentResponse, useEnrollment } from '../libs/ReactQueryHooks'
import { notify } from '../NotifyHelper'
import { SlackChannels } from '../../../pure-js/enums/SlackChannels'
import Box from './Box'
import CourseRegistrationApprovalWaiting from './CourseRegistrationApprovalWaiting'
import StripeCheckoutForm from './StripeCheckoutForm'
import { AbortButton, ClosedContainer, ContinueButton, OpenContainer } from './StyledComponents'
import { TextFieldWithLabelForm } from './TextFieldWithLabelForm'
import iconCompleted from '/iconCompleted.svg'
import bankIcon from '/public/bankIcon.svg'
import cards from '/public/cards.svg'
import iconArrow from '/public/iconArrow.svg'
import iconArrowLeft from '/public/iconArrowLeft.svg'
import swishBigLogo from '/public/swishBigLogo.svg'
import swishIcon from '/public/swishIcon.svg'
import vippsMobilePayHorizontal from '/public/Vipps_Pay_Mark_Name_RGB_290125.svg'
import vippsMobilePaySymbol from '/public/Vipps_Pay_Mark_Name_RGB_290125.svg'
import fritidskortetIcon from '/public/fritidskortetIcon.svg'
import epassiIcon from '/public/epassiIcon.svg'
import useIsMobile from '../hooks/useIsMobile'
import { useVippsCheckout } from '../hooks/useVippsCheckout'
import dayjs from 'dayjs'
import { VippsPaymentStatus, VippsPaymentStatusHollowInit } from '../../../pure-js/types/VippsMobileTypes'
import { useCurrency } from '../hooks/useCurrency'
import JsonModal from './JsonModal'
import { JsonIcon } from './Icons'
import { IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { SSN_REGEX } from '../helpers/CourseRegistrationStepOneHelper'
import { isValidNorwegianFnr } from '../helpers/NorwegianFnrHelper'
import useDeviceDetect from '../hooks/useDeviceDetect'
import { getStripeMinimumAmount, meetsStripeMinimum } from '../../../pure-js/config/stripeMinimumAmounts'

type Props = {
  state: State
  activity?: Activity
  onNextStep: () => void
  onOpenStepFour: () => void
  registerActiveStep?: number
  onCompleteRegistration: () => void
  onSetObfuscatedEnrollment: (obfuscatedEnrollment: EnrollmentWithPayment) => void
  onSetPaymentMethod: (paymentMethod: PaymentMethod) => void
  onSetIsPaid: (isPaid: boolean) => void
  onSetPaymentStatus: (paymentStatus: EnrollmentPaymentStatus) => void
  onSetMainUserSsn: (mainUserSsn: string) => void
  onSetPartnerUserSsn: (partnerUserSsn: string) => void
  onSetPayFor: (payFor: PayFor) => void
}

export const getValidationSchema = (client?: { country?: string } | null) =>
  Yup.object().shape({
    phoneNumber: Yup.string()
      .required(getGrooverText('registrationStep4PhoneRequired'))
      .test('is-valid-phone', getGrooverText('registrationStep4PhoneInvalid'), (value) => {
        if (!value) return false
        const defaultCountry = getCountryCodeFromClient(client)
        return isValidNumber(value, defaultCountry)
      })
  })

export const getReceiptSsnValidationSchema = (currency?: Currency) => {
  if (currency === 'NOK') {
    return Yup.object().shape({
      userSsnField: Yup.string()
        .required(getGrooverText('validationFnrRequiredNorway'))
        .test('is-valid-fnr', getGrooverText('validationFnrInvalidNorway'), (value) => {
          if (!value) return false
          return isValidNorwegianFnr(value)
        })
    })
  }

  // EUR and other non-Scandinavian currencies: no SSN required for receipts
  // Guard against undefined (still-loading) currency — fall through to SEK validation in that case
  if (currency && currency !== 'SEK') {
    return Yup.object().shape({
      userSsnField: Yup.string().notRequired().nullable()
    })
  }

  return Yup.object().shape({
    userSsnField: Yup.string()
      .required(getGrooverText('validationSsnRequiredSweden'))
      .matches(SSN_REGEX, getGrooverText('validationSsnInvalidSweden'))
  })
}

const FK_POLL_DURATION_MS = 15 * 60 * 1000
const FK_POLL_INTERVAL_MS = 15_000
const fkSessionKey = (enrollmentId: string) => `fk_session:${enrollmentId}`
const fkDeadlineKey = (enrollmentId: string) => `fk_deadline:${enrollmentId}`

const CourseRegistrationStepFour = ({
  state,
  activity,
  onOpenStepFour,
  onNextStep,
  registerActiveStep,
  onCompleteRegistration,
  onSetObfuscatedEnrollment,
  onSetPaymentMethod,
  onSetIsPaid,
  onSetPaymentStatus,
  onSetPayFor
}: Props) => {
  const [isLoading, setIsLoading] = useState(false)

  // Payment-method-specific error states
  const [swishError, setSwishError] = useState<{
    hasError: boolean
    errorCode?: string
  }>({ hasError: false })

  const [vippsError, setVippsError] = useState<{
    hasError: boolean
    textKey?: string
  }>({ hasError: false })

  const [stripeError, setStripeError] = useState<{
    hasError: boolean
    textKey?: string
  }>({ hasError: false })

  const [creditError, setCreditError] = useState<{
    hasError: boolean
    textKey?: string
  }>({ hasError: false })

  const [swishInstructionUUID, setSwishInstructionUUID] = useState('')
  const [swishToken, setSwishToken] = useState('')
  const [vippsPaymentReference, setVippsPaymentReference] = useState('')
  const [swishPhoneOption, setSwishPhoneOption] = useState<'registered' | 'custom'>('registered')
  const [swishAmountInCents, setSwishAmountInCents] = useState<number>(0)

  // Prevent concurrent Vipps checkout session creation (race condition protection)
  const creatingVippsSessionRef = useRef(false)
  // Ensure onCompleteRegistration is only called once when isPaid becomes true (avoids max update depth loop)
  const hasCalledCompleteRegistrationRef = useRef(false)
  const hasOpenedRegisterFlowFinalUrlRef = useRef(false)
  // Track whether the enrollment was already paid when this component first received monitor data.
  // Prevents the redirect from firing when navigating to a pre-existing paid enrollment.
  const hasReceivedFirstEnrollmentPublicDataRef = useRef(false)
  const wasAlreadyPaidOnFirstDataRef = useRef(false)
  const hasAutoAdvancedPaidStepRef = useRef(false)
  const hasResumedFritidskortetPollingRef = useRef(false)
  const userSelectedPayForRef = useRef(false)
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState<PaymentMethodFee[]>([])
  const [swishPaymentIntentStatus, setSwishPaymentIntentStatus] = useState<SwishPaymentStatus>(
    SwishPaymentStatus.PENDING
  )
  const [enrollmentPaidProcessing, setEnrollmentPaidProcessing] = useState(false)
  const [paidProcessingShowCancel, setPaidProcessingShowCancel] = useState(false)
  const paidProcessingCancelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [vippsPaymentStatus, setVippsPaymentStatus] = useState<
    VippsPaymentStatus | VippsPaymentStatusHollowInit | undefined
  >(undefined)
  const [vippsCheckoutSessionActive, setVippsCheckoutSessionActive] = useState(false)
  const [stripeConfig, setStripeConfig] = useState<StripeConfig>()
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null)
  const [stripeOptions, setStripeOptions] = useState<stripeJs.StripeElementsOptions>()

  // Extract paymentIntentId from clientSecret format: "pi_XXXXX_secret_YYYYY"
  const stripePaymentIntentId = useMemo(() => stripeClientSecret?.split('_secret_')[0] ?? null, [stripeClientSecret])
  const stripePaymentIntentStatus = useStripePaymentIntentStatus(stripePaymentIntentId)
  const [payFor, setPayFor] = useState<PayFor>(PayFor.Main)
  const [toBePaid, setToBePaid] = useState<Record<PayFor, number>>({} as Record<PayFor, number>)
  const [userCredit, setUserCredit] = useState(0)
  const [jsonModalOpen, setJsonModalOpen] = useState(false)
  const [jsonModalData, setJsonModalData] = useState<any>(null)
  const [fritidskortetSubmitting, setFritidskortetSubmitting] = useState(false)
  const [fritidskortetGuardianSsn, setFritidskortetGuardianSsn] = useState('')
  const [fritidskortetChildSsn, setFritidskortetChildSsn] = useState('')
  const [fritidskortetPolling, setFritidskortetPolling] = useState(false)
  const [fritidskortetConfirmClicked, setFritidskortetConfirmClicked] = useState(false)
  const [fritidskortetError, setFritidskortetError] = useState<string | null>(null)
  const fritidskortetPollingActiveRef = useRef(false)
  const [fritidskortetSessionId, setFritidskortetSessionId] = useState<string | null>(null)
  const [epassiSubmitting, setEpassiSubmitting] = useState(false)
  const [epassiError, setEpassiError] = useState<string | null>(null)
  const [epassiStamp, setEpassiStamp] = useState<string | null>(null)
  const [epassiWaiting, setEpassiWaiting] = useState(false)
  const epassiFormRef = useRef<HTMLFormElement>(null)
  const hasHandledEpassiReturnRef = useRef(false)
  const isMobile = useIsMobile()
  const { isMobile: isPhoneDevice } = useDeviceDetect()
  const { isLoaded: isVippsLoaded, isLoading: isVippsLoading, error: vippsLoadError } = useVippsCheckout()
  const enrollmentUserIdFromUrl = useMemo(
    () => new URLSearchParams(window.location.search).get('u')?.trim() || undefined,
    []
  )

  // Check if we're in development/localhost mode
  const isDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

  const {
    data: obfuscatedEnrollmentResponse,
    refetch: refetchEnrollment,
    isFetching: isEnrollmentFetching,
    isFetched: isEnrollmentFetched,
    isError: isEnrollmentError
  } = useEnrollment(state, enrollmentUserIdFromUrl)
  const enrollment = isGetObfuscatedEnrollmentResponse(obfuscatedEnrollmentResponse)
    ? obfuscatedEnrollmentResponse.enrollment
    : undefined
  const groupMembers = isGetObfuscatedEnrollmentResponse(obfuscatedEnrollmentResponse)
    ? obfuscatedEnrollmentResponse.groupMembers
    : undefined

  // Get client from activity or from enrollment if activity is not available
  const clientFromActivity = useClient(activity)
  const clientFromEnrollment = useClient(enrollment?.activities?.[0])
  const client = clientFromActivity || clientFromEnrollment
  const currency = useCurrency(client)
  const isRegisterFlowSwishAmountEditable = client?.registerFlowEditableSwishAmount === true

  // Get current language - prioritize localStorage (reflects URL param) over stale state
  const localStorageLanguage = getLanguageCode()
  const currentLanguage = localStorageLanguage || state.user?.language || state.selectedLanguage || 'en'

  // Map our language codes to Vipps supported languages
  // Our: 'sv' | 'en' | 'no' -> Vipps: 'en' | 'nb' | 'fi' | 'dk'
  // Note: Vipps doesn't support Swedish, so we use Norwegian (mutually intelligible)
  const getVippsLanguage = (): 'en' | 'nb' | 'fi' | 'dk' => {
    switch (currentLanguage) {
      case 'no':
      case 'sv': // Swedish -> Norwegian (languages are mutually intelligible)
        return 'nb' // Norwegian Bokmål
      case 'en':
      default:
        return 'en'
    }
  }

  const activePaidFor = useCallback(
    (payFor: PayFor) => {
      return (toBePaid[payFor] || 0) > 0
    },
    [toBePaid]
  )

  // initialize payment monitoring (by default with empty string, will use real instructionUUID as soon as we have it)
  let swishPaymentIntentMonitor = useSwishPaymentIntentStatus(swishInstructionUUID)
  let vippsPaymentIntentMonitor = useVippsPaymentStatus(vippsPaymentReference)

  const openRegisterFlowFinalUrl = useCallback(
    (forceRetry = false) => {
      if (hasOpenedRegisterFlowFinalUrlRef.current && !forceRetry) return

      const rawUrl = client?.registerFlowFinalUrl?.trim()
      if (!rawUrl) return
      const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`
      const enrollmentId = state.obfuscatedEnrollment?.id || state.enrollmentId

      try {
        const parsed = new URL(normalizedUrl)
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          return
        }
        if (enrollmentId) {
          parsed.searchParams.set('enrollmentId', enrollmentId)
        }
        const openedWindow = window.open(parsed.toString(), '_blank', 'noopener,noreferrer')
        if (openedWindow) {
          hasOpenedRegisterFlowFinalUrlRef.current = true
        }
      } catch (error) {
        console.warn('Skipping invalid registerFlowFinalUrl', { rawUrl, error })
      }
    },
    [client?.registerFlowFinalUrl, state.enrollmentId, state.obfuscatedEnrollment?.id]
  )

  const completeAfterSuccessfulPayment = useCallback(() => {
    if (hasCalledCompleteRegistrationRef.current) return
    hasCalledCompleteRegistrationRef.current = true
    openRegisterFlowFinalUrl()
    onCompleteRegistration()
  }, [onCompleteRegistration, openRegisterFlowFinalUrl])

  // Show "Network issues? Back to previous screen" escape hatch after 15s of paid-processing overlay
  useEffect(() => {
    if (enrollmentPaidProcessing) {
      paidProcessingCancelTimerRef.current = setTimeout(() => {
        setPaidProcessingShowCancel(true)
      }, 15000)
    } else {
      if (paidProcessingCancelTimerRef.current) {
        clearTimeout(paidProcessingCancelTimerRef.current)
        paidProcessingCancelTimerRef.current = null
      }
      setPaidProcessingShowCancel(false)
    }
    return () => {
      if (paidProcessingCancelTimerRef.current) {
        clearTimeout(paidProcessingCancelTimerRef.current)
      }
    }
  }, [enrollmentPaidProcessing])

  const onHandleNext = () => {
    onSetPayFor(payFor)
    onNextStep()
    onCompleteRegistration()
  }

  // monitor enrollment public document
  const enrollmentPublicMonitor = useEnrollmentPublic(
    !state.enrollmentId ? state.obfuscatedEnrollment?.id : state.enrollmentId
  )

  useEffect(() => {
    if (!enrollmentPublicMonitor.data) return
    if (enrollmentPublicMonitor.status == 'success') {
      // if monitor listening is not error
      const { paymentStatus, updatedAt } = enrollmentPublicMonitor.data as EnrollmentPublic
      onSetPaymentStatus(paymentStatus)
      if (paymentStatus === 'paid') {
        refetchEnrollment()
        // If the very first data we receive is already 'paid', the payment happened before this
        // component mounted (e.g. returning to a paid enrollment). Mark it so we skip the redirect.
        if (!hasReceivedFirstEnrollmentPublicDataRef.current) {
          wasAlreadyPaidOnFirstDataRef.current = true
        }
        if (!wasAlreadyPaidOnFirstDataRef.current) {
          console.log('enrollment transitioned to paid')
          setEnrollmentPaidProcessing(true)
          onSetIsPaid(true)
        }
      }
      hasReceivedFirstEnrollmentPublicDataRef.current = true
      if (dayjs(updatedAt).isBefore(dayjs())) {
        console.log('enrollment is updated')
        refetchEnrollment()
      }
    }
  }, [state.enrollmentId, enrollmentPublicMonitor.data])

  useEffect(() => {
    if (!state.isPaid) return
    if (wasAlreadyPaidOnFirstDataRef.current) return // pre-existing payment — skip redirect
    completeAfterSuccessfulPayment()
  }, [state.isPaid, completeAfterSuccessfulPayment])

  useEffect(() => {
    if (registerActiveStep !== 4) {
      hasAutoAdvancedPaidStepRef.current = false
    }
  }, [registerActiveStep])

  // Opening step 4 while already paid used to require clicking Continue; advance to step 5 automatically.
  useEffect(() => {
    if (registerActiveStep !== 4 || !state.isPaid || hasAutoAdvancedPaidStepRef.current) return
    hasAutoAdvancedPaidStepRef.current = true
    openRegisterFlowFinalUrl(true)
    onNextStep()
  }, [registerActiveStep, state.isPaid, openRegisterFlowFinalUrl, onNextStep])

  // Fritidskortet: read pending betalsession from URL (?fk={betalsession}) set on payment submit
  // Falls back to localStorage so the session survives the EHM redirect round-trip across tabs
  const fkSessionId = useMemo(() => {
    if (typeof window === 'undefined') return null
    const enrollmentId = state.obfuscatedEnrollment?.id || state.enrollmentId
    return (
      new URLSearchParams(window.location.search).get('fk') ||
      (enrollmentId ? localStorage.getItem(fkSessionKey(enrollmentId)) : null) ||
      null
    )
  }, [state.obfuscatedEnrollment?.id, state.enrollmentId])

  // ePassi: detect return/cancel/reject redirects from ePassi service via URL query params
  const epassiUrlAction = useMemo(() => {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    if (params.get('epassi_return') === '1') return 'return' as const
    if (params.get('epassi_cancel') === '1') return 'cancel' as const
    if (params.get('epassi_reject') === '1') return 'reject' as const
    return null
  }, [])

  const epassiRejectError = useMemo(() => {
    if (typeof window === 'undefined' || epassiUrlAction !== 'reject') return null
    const params = new URLSearchParams(window.location.search)
    return params.get('ERROR') || null
  }, [epassiUrlAction])

  // Force Vipps button to truly remount when language changes.
  // The web component doesn't reliably observe attribute changes, so we change the React key.
  const [vippsRemountKey, setVippsRemountKey] = useState(0)
  useEffect(() => {
    setVippsRemountKey((prev) => prev + 1)
  }, [currentLanguage])

  const focusedEnrollmentId = state.obfuscatedEnrollment?.id || state.enrollmentId || ''

  // Skeleton while getObfuscatedEnrollment is in flight; error+retry once it has failed (e.g. backend down).
  const hasEnrollmentPriceSource = !!(enrollment || state.obfuscatedEnrollment?.id)
  const isAwaitingEnrollmentPrice = !!focusedEnrollmentId && !hasEnrollmentPriceSource && !(PayFor.Both in toBePaid)
  const isPriceLoading = isAwaitingEnrollmentPrice && (!isEnrollmentFetched || isEnrollmentFetching)
  const isEnrollmentPriceError =
    isAwaitingEnrollmentPrice && isEnrollmentError && isEnrollmentFetched && !isEnrollmentFetching

  useEffect(() => {
    if (!enrollment?.id) return
    if (state.obfuscatedEnrollment?.id === enrollment.id) return
    onSetObfuscatedEnrollment(enrollment)
  }, [enrollment, state.obfuscatedEnrollment?.id, onSetObfuscatedEnrollment])

  // Check if hardCoupleIndividualParticipantFlow is enabled - when enabled, "Pay for Both" is not allowed
  const isHardCoupleIndividualFlow =
    client?.featureToggles?.hardCoupleIndividualParticipantFlow === true &&
    !!(enrollment?.partnerUser?.id || state.obfuscatedEnrollment?.partnerUser?.id)

  const sourceEnrollmentForPayment = !!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
  const isGroupMembersLoading =
    !!sourceEnrollmentForPayment?.enrollmentGroupId && isEnrollmentFetching && groupMembers === undefined
  const isPaymentPriceBlocked = isPriceLoading || isGroupMembersLoading || isEnrollmentPriceError
  const paymentView = useMemo(() => {
    if (!sourceEnrollmentForPayment) return null
    return resolveEnrollmentPaymentView({
      focusedEnrollment: sourceEnrollmentForPayment,
      members: groupMembers ?? [],
      allowPayForBoth: client?.allowPayForBoth,
      hardCoupleIndividualParticipantFlow: isHardCoupleIndividualFlow
    })
  }, [sourceEnrollmentForPayment, groupMembers, client?.allowPayForBoth, isHardCoupleIndividualFlow])

  useEffect(() => {
    userSelectedPayForRef.current = false
  }, [focusedEnrollmentId])

  const onSelectPayFor = useCallback((selection: PayFor) => {
    userSelectedPayForRef.current = true
    setPayFor(selection)
  }, [])

  useEffect(() => {
    if (!enrollment && !state.obfuscatedEnrollment?.id) return

    const source = !!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
    if (!source) return

    let toBePaidData: Record<PayFor, number>
    if (paymentView?.kind === 'grouped' && paymentView.members.length >= 2 && focusedEnrollmentId) {
      toBePaidData = getGroupToBePaidData(paymentView.members, focusedEnrollmentId)
    } else {
      toBePaidData = getToBePaidData(source, false)
    }
    setToBePaid(toBePaidData)
  }, [enrollment, state.obfuscatedEnrollment, paymentView, focusedEnrollmentId])

  const isGroupedWithoutBoth = paymentView?.kind === 'grouped' && !paymentView.showPayForGroup

  // Compute the effective payFor value - ensures PayFor.Both is never used when feature toggle is enabled
  // This is a safeguard against race conditions where payFor state might not have updated yet
  const effectivePayFor = useMemo(() => {
    // When using ?u= parameter, determine payFor based on which user is actually paying
    if (enrollmentUserIdFromUrl) {
      const source = state.obfuscatedEnrollment || enrollment
      if (source) {
        const isMainUser = source.mainUser?.id === enrollmentUserIdFromUrl
        const isPartnerUser = source.partnerUser?.id === enrollmentUserIdFromUrl

        if (isPartnerUser) {
          return PayFor.Partner
        } else if (isMainUser) {
          return PayFor.Main
        }
        // enrollmentUserIdFromUrl doesn't match any user — will fall through to default logic
        console.warn('effectivePayFor: enrollmentUserIdFromUrl does not match main or partner user', {
          enrollmentUserIdFromUrl,
          mainUserId: source.mainUser?.id,
          partnerUserId: source.partnerUser?.id
        })
      } else {
        // Enrollment data not loaded yet — will fall through to default payFor logic.
        // setupStripe guards against proceeding in this state.
        console.warn('effectivePayFor: ?u= parameter set but enrollment data not yet available, using fallback payFor')
      }
    }

    if (isHardCoupleIndividualFlow && payFor === PayFor.Both) {
      // Force to PayFor.Main (or Partner if Main has nothing to pay)
      return activePaidFor(PayFor.Main) ? PayFor.Main : PayFor.Partner
    }
    if (isGroupedWithoutBoth && payFor === PayFor.Both) {
      return PayFor.Main
    }
    return payFor
  }, [
    isHardCoupleIndividualFlow,
    isGroupedWithoutBoth,
    payFor,
    activePaidFor,
    enrollmentUserIdFromUrl,
    state.obfuscatedEnrollment,
    enrollment
  ])

  const paymentEnrollmentId = useMemo(() => {
    if (paymentView?.kind === 'grouped' && focusedEnrollmentId) {
      return resolveGroupPaymentEnrollmentId(paymentView.members, focusedEnrollmentId, effectivePayFor)
    }
    return focusedEnrollmentId
  }, [paymentView, focusedEnrollmentId, effectivePayFor])

  const apiPayFor = useMemo((): PayFor => {
    if (paymentView?.kind === 'grouped' && effectivePayFor === PayFor.Partner) {
      return PayFor.Main
    }
    return effectivePayFor
  }, [paymentView, effectivePayFor])

  const isGroupedPayForBoth =
    paymentView?.kind === 'grouped' && effectivePayFor === PayFor.Both && paymentView.showPayForGroup

  // True when there are non-excluded activities with a positive finalPrice that are not yet
  // confirmed (e.g. pending admin approval). toBePaid excludes these, so a 0 balance would
  // otherwise look like "nothing to pay" — but the user will owe money once approved.
  // Used both to block the auto-advance effect and to show the approval-waiting UI in the render.
  const hasPendingPositiveAmount = useMemo(() => {
    const source = state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
    if (!source) return false
    return hasPendingPricedActivities(source)
  }, [enrollment, state.obfuscatedEnrollment])

  // True when the confirmed (not-pending, not-excluded) portion of the enrollment already
  // has a positive balance owed. For partly-approved enrollments this lets the user pay for
  // the approved slice immediately even while other activities are still pending admin approval.
  const hasConfirmedAmountDue = useMemo(() => {
    if (!(PayFor.Both in toBePaid)) return false
    return (toBePaid[PayFor.Both] || 0) > 0
  }, [toBePaid])

  // Auto-advance to step 5 when the confirmed amount is 0 — no payment needed.
  // isActivityFree() is a static check on activity.price and fires before the enrollment exists,
  // so it cannot catch cases where toBePaid resolves to 0 after enrollment creation (e.g. discounts).
  useEffect(() => {
    if (registerActiveStep !== 4) return
    if (isPaymentPriceBlocked) return
    if (!enrollment && !state.obfuscatedEnrollment?.id) return
    if (state.isPaid) return

    // Guard against the race where toBePaid is still {} (initial state).
    // PayFor.Both is always present once toBePaid is populated, so use it as the sentinel.
    if (!(PayFor.Both in toBePaid)) return

    // Determine which slice to inspect:
    //
    // ?u= flow — the URL identifies a specific participant. effectivePayFor is resolved
    //   correctly from the actual user ID once enrollment is loaded (the guard above ensures
    //   enrollment data is available). Checking only that user's slice is correct because
    //   they are responsible solely for their own portion.
    //
    // Standard flow — use the WHOLE enrollment (PayFor.Both), NOT effectivePayFor.
    //   Reason: the payFor-defaulting effect (defined later in this file) runs AFTER this
    //   effect in the same React flush. On the render where toBePaid first populates, it may
    //   still hold effectivePayFor = PayFor.Partner (set during the initial render when
    //   toBePaid = {} made all activePaidFor() checks return false). For a single-user
    //   enrollment, toBePaid[Partner] = 0 even though main still owes money — checking
    //   PayFor.Both avoids that false positive.
    const sliceToCheck = enrollmentUserIdFromUrl ? effectivePayFor : PayFor.Both

    if (!(sliceToCheck in toBePaid)) return
    const amount = toBePaid[sliceToCheck]
    if (amount > 0) return

    // toBePaid excludes pending (not-yet-approved) activities. If there are pending priced
    // activities, only advance when the user has actually paid for the confirmed slice —
    // otherwise a user who selected only pending activities (0 confirmed due, 0 paid) would
    // reach Step 5 without ever paying.
    if (hasPendingPositiveAmount) {
      const source = state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
      if (!source) return
      const { paid } = getUserPaymentStatus(source, sliceToCheck)
      if (paid <= 0) return
    }

    onSetIsPaid(true)
    onSetPayFor(payFor)
    onNextStep()
    if (!hasCalledCompleteRegistrationRef.current) {
      hasCalledCompleteRegistrationRef.current = true
      onCompleteRegistration()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payFor, onSetIsPaid, onSetPayFor, onNextStep, onCompleteRegistration intentionally omitted
  }, [
    registerActiveStep,
    isPriceLoading,
    toBePaid,
    effectivePayFor,
    enrollment,
    state.obfuscatedEnrollment?.id,
    state.isPaid,
    enrollmentUserIdFromUrl,
    hasPendingPositiveAmount
  ])

  const fritidskortetPayForApi = useMemo((): 'mainUser' | 'partnerUser' | 'both' => {
    if (apiPayFor === PayFor.Main) return 'mainUser'
    if (apiPayFor === PayFor.Partner) return 'partnerUser'
    return 'both'
  }, [apiPayFor])

  const epassiPayForApi = useMemo((): 'mainUser' | 'partnerUser' | 'both' => {
    if (apiPayFor === PayFor.Main) return 'mainUser'
    if (apiPayFor === PayFor.Partner) return 'partnerUser'
    return 'both'
  }, [apiPayFor])

  const fritidskortetAlreadyUsedForEnrollment = useMemo(() => {
    const source = state.obfuscatedEnrollment ?? enrollment
    const payments = source?.payments ?? []
    return payments.some(
      (p) => p.type === 'pay' && (p.paymentMethod === PaymentMethod.Fritidskortet || p.source === 'fritidskortet')
    )
  }, [state.obfuscatedEnrollment?.payments, enrollment?.payments])

  const startFritidskortetPolling = useCallback(
    (pollEnrollmentId: string, paymentSessionId: string) => {
      // Prevent duplicate polling loops (e.g. StrictMode double-invoke or dep change recreating the callback)
      if (fritidskortetPollingActiveRef.current) {
        console.warn('[Fritidskortet] Polling already active, ignoring duplicate startFritidskortetPolling call')
        return
      }
      fritidskortetPollingActiveRef.current = true
      let cancelled = false // cancellation flag for in-flight setTimeout callbacks
      let pollIndex = 0

      const clearFkSession = () => {
        cancelled = true
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', window.location.pathname)
          localStorage.removeItem(fkSessionKey(pollEnrollmentId))
          localStorage.removeItem(fkDeadlineKey(pollEnrollmentId))
        }
        setFritidskortetSessionId(null)
        setFritidskortetConfirmClicked(false)
        fritidskortetPollingActiveRef.current = false
      }

      // Like clearFkSession but keeps the panel open (paymentMethod stays Fritidskortet).
      // Resets sessionId so the stepper goes back to 'submit' stage for a fresh retry.
      const clearFkSessionKeepPanel = () => {
        cancelled = true
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', window.location.pathname)
          localStorage.removeItem(fkSessionKey(pollEnrollmentId))
          localStorage.removeItem(fkDeadlineKey(pollEnrollmentId))
        }
        setFritidskortetSessionId(null)
        setFritidskortetConfirmClicked(false)
        fritidskortetPollingActiveRef.current = false
      }
      setFritidskortetPolling(true)
      setFritidskortetError(null)
      // Persist deadline so it survives the EHM redirect round-trip.
      // On first call: store a fresh deadline. On resume: reuse the stored one.
      const storedDeadline = localStorage.getItem(fkDeadlineKey(pollEnrollmentId))
      const deadline = storedDeadline ? parseInt(storedDeadline, 10) : Date.now() + FK_POLL_DURATION_MS
      if (!storedDeadline) {
        localStorage.setItem(fkDeadlineKey(pollEnrollmentId), String(deadline))
      }
      console.info('[Fritidskortet][client]', {
        event: 'polling_started',
        enrollmentId: pollEnrollmentId,
        paymentSessionId,
        deadlineIso: new Date(deadline).toISOString()
      })
      const isNearDeadline = () => Date.now() + FK_POLL_INTERVAL_MS >= deadline
      const runPollLoop = async (): Promise<void> => {
        while (true) {
          if (cancelled) return
          pollIndex += 1
          let res: Awaited<ReturnType<typeof fritidskortetGetPaymentStatus>>
          try {
            res = await fritidskortetGetPaymentStatus({ enrollmentId: pollEnrollmentId, paymentSessionId })
          } catch (err) {
            // Network/unexpected error — log and retry after delay
            console.error('Fritidskortet getPaymentStatus exception:', err)
            console.info('[Fritidskortet][client]', {
              event: 'poll_getPaymentStatus_throw',
              enrollmentId: pollEnrollmentId,
              paymentSessionId,
              pollIndex
            })
            clearFkSessionKeepPanel()
            setFritidskortetPolling(false)
            setFritidskortetError(getGrooverText('registrationStep5PaymentFritidskortetStatusError'))
            return
          }
          if ('isError' in res) {
            // Function returned an error response — retry after delay instead of stopping
            console.error('Fritidskortet getPaymentStatus error:', res)
            console.info('[Fritidskortet][client]', {
              event: 'poll_getPaymentStatus_isError',
              enrollmentId: pollEnrollmentId,
              paymentSessionId,
              pollIndex,
              message: res.message
            })
            if (isNearDeadline()) {
              setFritidskortetError(res.message || getGrooverText('registrationStep5PaymentFritidskortetStatusError'))
              clearFkSessionKeepPanel()
              setFritidskortetPolling(false)
              return
            }
            await new Promise<void>((r) => setTimeout(r, FK_POLL_INTERVAL_MS))
            continue
          }
          console.info('[Fritidskortet][client]', {
            event: 'poll_status',
            enrollmentId: pollEnrollmentId,
            paymentSessionId,
            pollIndex,
            status: res.status,
            approvedAmountInOre: res.approvedAmountInOre
          })
          if (res.status === 'payment_approved') {
            const approvedInOre = res.approvedAmountInOre ?? 0
            if (approvedInOre <= 0) {
              console.info('[Fritidskortet][client]', {
                event: 'confirm_skipped_zero_approved_amount',
                enrollmentId: pollEnrollmentId,
                paymentSessionId,
                pollIndex,
                approvedInOre
              })
              clearFkSessionKeepPanel()
              setFritidskortetPolling(false)
              setFritidskortetError(getGrooverText('registrationStep5PaymentFritidskortetStatusError'))
              return
            }
            console.info('[Fritidskortet][client]', {
              event: 'confirm_payout_invoking',
              enrollmentId: pollEnrollmentId,
              paymentSessionId,
              pollIndex,
              approvedAmountInOre: approvedInOre
            })
            const confirmRes = await fritidskortetConfirmPayout({
              enrollmentId: pollEnrollmentId,
              paymentSessionId,
              approvedAmountInOre: approvedInOre,
              payFor: fritidskortetPayForApi
            })
            clearFkSession()
            setFritidskortetPolling(false)
            if ('isError' in confirmRes) {
              setFritidskortetError(
                confirmRes.message || getGrooverText('registrationStep5PaymentFritidskortetStatusError')
              )
              console.error('Fritidskortet confirmPayout error:', confirmRes)
              console.info('[Fritidskortet][client]', {
                event: 'confirm_payout_error',
                enrollmentId: pollEnrollmentId,
                paymentSessionId,
                pollIndex,
                message: confirmRes.message
              })
              return
            }
            console.info('[Fritidskortet][client]', {
              event: 'confirm_payout_ok',
              enrollmentId: pollEnrollmentId,
              paymentSessionId,
              pollIndex
            })
            // Refetch to get updated payments and remaining balance
            const freshResult = await refetchEnrollment()
            const freshEnrollment = isGetObfuscatedEnrollmentResponse(freshResult.data)
              ? freshResult.data.enrollment
              : undefined
            if (freshEnrollment) {
              const remaining = getToBePaidData(freshEnrollment, false)
              const remainingAmount = remaining[effectivePayFor] || 0
              if (remainingAmount > 0) {
                // Partial payment — update toBePaid and let user pick another method
                // (fritidskortetAlreadyUsedForEnrollment will become true from updated payments,
                // hiding the Fritidskortet option and showing remaining balance)
                return
              }
            }
            onSetIsPaid(true)
            return
          }
          if (['cancelled', 'rejected', 'expired'].includes(res.status)) {
            // Terminal status — clear session keys but keep panel open so error is visible
            clearFkSessionKeepPanel()
            setFritidskortetPolling(false)
            setFritidskortetError(
              `${getGrooverText('registrationStep5PaymentFritidskortetStatusError')} (reason: ${res.status})`
            )
            return
          }
          if (isNearDeadline()) {
            clearFkSessionKeepPanel()
            setFritidskortetPolling(false)
            setFritidskortetError(getGrooverText('registrationStep5PaymentFritidskortetStatusTimeout'))
            return
          }
          await new Promise<void>((r) => setTimeout(r, FK_POLL_INTERVAL_MS))
        }
      }
      withFritidskortetPollLock(paymentSessionId, runPollLoop).catch((err: unknown) => {
        console.error('[Fritidskortet] poll loop crashed:', err)
        clearFkSessionKeepPanel()
        setFritidskortetPolling(false)
        setFritidskortetError(getGrooverText('registrationStep5PaymentFritidskortetStatusError'))
      })
    },
    [fritidskortetPayForApi, onSetIsPaid, refetchEnrollment, effectivePayFor]
  )

  // On mount: if URL has ?fk={betalsession} or localStorage has fk_session, resume polling
  useEffect(() => {
    if (!fkSessionId) return
    const enrollmentId = state.obfuscatedEnrollment?.id || state.enrollmentId
    if (!enrollmentId) return
    if (hasResumedFritidskortetPollingRef.current) return
    hasResumedFritidskortetPollingRef.current = true
    console.log(
      `[Fritidskortet] Resumed polling - paymentSessionId=${fkSessionId}, deadline=${new Date(parseInt(localStorage.getItem(fkDeadlineKey(enrollmentId)) || '0', 10)).toISOString()}`
    )
    setFritidskortetConfirmClicked(false)
    setFritidskortetSessionId(fkSessionId)
    startFritidskortetPolling(enrollmentId, fkSessionId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fkSessionId, state.obfuscatedEnrollment?.id, state.enrollmentId, startFritidskortetPolling])

  // On mount: handle ePassi return/cancel/reject URL redirects
  useEffect(() => {
    if (!epassiUrlAction) return
    if (hasHandledEpassiReturnRef.current) return
    hasHandledEpassiReturnRef.current = true

    // Clean URL params regardless of action
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.delete('epassi_return')
      url.searchParams.delete('epassi_cancel')
      url.searchParams.delete('epassi_reject')
      url.searchParams.delete('ERROR')
      url.searchParams.delete('STAMP')
      window.history.replaceState(
        null,
        '',
        url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '')
      )
    }

    if (epassiUrlAction === 'return') {
      // User completed payment on ePassi — show waiting state while webhook confirms
      onSetPaymentMethod(PaymentMethod.Epassi)
      setEpassiWaiting(true)
      setEpassiError(null)
    } else if (epassiUrlAction === 'cancel') {
      // User cancelled on ePassi — return to payment method selection
      onSetPaymentMethod(PaymentMethod.Epassi)
      setEpassiError(null)
      setEpassiWaiting(false)
    } else if (epassiUrlAction === 'reject') {
      // ePassi rejected the payment — show localized error
      onSetPaymentMethod(PaymentMethod.Epassi)
      setEpassiWaiting(false)
      let errorMessage: string
      switch (epassiRejectError) {
        case 'INVALID_REQUEST':
          errorMessage = getGrooverText('registrationStep5PaymentEpassiRejectInvalidRequest')
          break
        case 'INVALID_REQUEST_SIGNATURE':
          errorMessage = getGrooverText('registrationStep5PaymentEpassiRejectInvalidSignature')
          break
        case 'SITE_DOES_NOT_EXIST':
          errorMessage = getGrooverText('registrationStep5PaymentEpassiRejectSiteNotFound')
          break
        default:
          // STAMP present but no ERROR means user error (e.g. insufficient saldo)
          errorMessage = getGrooverText('registrationStep5PaymentEpassiRejectGeneric')
          break
      }
      setEpassiError(errorMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ePassi: when waiting for webhook confirmation, monitor enrollmentPublic for paymentStatus changes
  useEffect(() => {
    if (!epassiWaiting) return
    if (!enrollmentPublicMonitor.data) return
    const { paymentStatus } = enrollmentPublicMonitor.data as EnrollmentPublic
    if (paymentStatus === 'paid') {
      setEpassiWaiting(false)
      setEpassiError(null)
      onSetIsPaid(true)
    }
  }, [epassiWaiting, enrollmentPublicMonitor.data, onSetIsPaid])

  const handleFritidskortetPay = useCallback(async () => {
    const log = (msg: string, data?: unknown) => {
      console.warn('[Fritidskortet]', msg, data !== undefined ? data : '')
    }
    const enrollmentId = paymentEnrollmentId
    // Clear any stale session keys from a previous incomplete flow before starting fresh
    if (enrollmentId && typeof window !== 'undefined') {
      localStorage.removeItem(fkSessionKey(enrollmentId))
      localStorage.removeItem(fkDeadlineKey(enrollmentId))
    }
    if (!enrollmentId || !client?.orgNo || !client?.bankGiro) {
      log('Aborted: missing enrollment or client details', {
        hasEnrollmentId: !!enrollmentId,
        hasOrgNo: !!client?.orgNo,
        hasBankGiro: !!client?.bankGiro
      })
      setFritidskortetError(getGrooverText('validationGenericError'))
      return
    }
    const guardianSsn = fritidskortetGuardianSsn.replace(/\s/g, '').trim()
    if (!SSN_REGEX.test(guardianSsn)) {
      log('Aborted: invalid guardian SSN format')
      setFritidskortetError(getGrooverText('validationSsnInvalidSweden'))
      return
    }
    const childSsn = fritidskortetChildSsn.replace(/\s/g, '').trim()
    if (!SSN_REGEX.test(childSsn)) {
      log('Aborted: invalid child SSN format')
      setFritidskortetError(getGrooverText('validationSsnInvalidSweden'))
      return
    }
    const fee = resolveAdministrationFeeAmount(PaymentMethod.Fritidskortet, client, toBePaid[effectivePayFor] || 0)
    const totalInOre = (toBePaid[effectivePayFor] || 0) + fee
    const { fritidskortetCoversInOre: amountInOre } = calcFritidskortetAmounts(totalInOre)
    const paymentReference = state?.obfuscatedEnrollment?.ocr || enrollment?.ocr || ''
    if (!paymentReference) {
      log('Aborted: missing payment reference (OCR)')
      setFritidskortetError(getGrooverText('validationGenericError'))
      return
    }
    setFritidskortetSubmitting(true)
    setFritidskortetError(null)
    setFritidskortetSessionId(null) // reset stepper to 'submit' stage on retry
    fritidskortetPollingActiveRef.current = false // reset so new polling loop can start
    const uParam = new URLSearchParams(window.location.search).get('u')
    const returUrl = `${window.location.origin}${window.location.pathname}${uParam ? `?u=${encodeURIComponent(uParam)}` : ''}`
    const submitRequest: FritidskortetSubmitPaymentDetailsRequest = {
      enrollmentId,
      payFor: fritidskortetPayForApi,
      guardianPersonnummer: guardianSsn,
      childPersonnummer: childSsn,
      orgNo: client.orgNo,
      bankGiro: client.bankGiro,
      paymentReference,
      amountInOre,
      returUrl
    }
    log('Calling submitPaymentDetails', submitRequest)
    try {
      const res = await fritidskortetSubmitPaymentDetails(submitRequest)
      if ('isError' in res) {
        const msg = res.message || getGrooverText('registrationStep5PaymentFritidskortetStatusError')
        log('Submit error', res)
        setFritidskortetError(msg)
        return
      }
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', `${window.location.pathname}?fk=${res.paymentSessionId}`)
        localStorage.setItem(fkSessionKey(enrollmentId), res.paymentSessionId)
      }
      setFritidskortetSessionId(res.paymentSessionId)
      startFritidskortetPolling(enrollmentId, res.paymentSessionId)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      const msg = error.message || getGrooverText('registrationStep5PaymentFritidskortetStatusError')
      log('Submit exception', error)
      setFritidskortetError(msg)
    } finally {
      setFritidskortetSubmitting(false)
    }
  }, [
    paymentEnrollmentId,
    state?.obfuscatedEnrollment?.ocr,
    client,
    fritidskortetGuardianSsn,
    fritidskortetChildSsn,
    fritidskortetPayForApi,
    startFritidskortetPolling,
    toBePaid,
    effectivePayFor,
    enrollment?.ocr
  ])

  const submitEpassiForm = (data: EpassiInitiatePaymentResponse) => {
    setEpassiStamp(data.stamp)
    // Build and auto-submit hidden form to ePassi
    const form = epassiFormRef.current
    if (!form) return
    form.action = data.formActionUrl
    form.method = 'POST'
    // Clear previous hidden fields
    while (form.firstChild) form.removeChild(form.firstChild)
    const fields: Record<string, string | undefined> = {
      STAMP: data.stamp,
      SITE: data.site,
      AMOUNT: data.amount,
      FEE: data.fee,
      VAT_VALUE: data.vatValue,
      RETURN: data.returnUrl,
      CANCEL: data.cancelUrl,
      REJECT: data.rejectUrl,
      NOTIFY_URL: data.notifyUrl,
      MAC: data.mac
    }
    for (const [key, value] of Object.entries(fields)) {
      if (value === undefined) continue
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = value
      form.appendChild(input)
    }
    form.submit()
  }

  const handleEpassiPay = useCallback(async () => {
    const enrollmentId = paymentEnrollmentId
    if (!enrollmentId) {
      setEpassiError(getGrooverText('registrationStep5PaymentEpassiStatusError'))
      return
    }
    setEpassiSubmitting(true)
    setEpassiError(null)
    setEpassiStamp(null)
    try {
      // V4 grouped "Pay for both" routes to the group callable; the returned
      // `response` carries the same ePassi form fields as the single-enrollment
      // path, so form submission is shared.
      if (isGroupedPayForBoth && paymentView?.kind === 'grouped' && focusedEnrollmentId) {
        const res = await payForGroupWithEpassi({
          enrollmentGroupId: paymentView.enrollmentGroupId,
          payerEnrollmentId: focusedEnrollmentId
        })
        if (!res.ok) {
          setEpassiError(res.message || getGrooverText('registrationStep5PaymentEpassiStatusError'))
          return
        }
        submitEpassiForm(res.response)
        return
      }

      const res = await epassiInitiatePayment({ enrollmentId, payFor: epassiPayForApi })
      if ('isError' in res) {
        setEpassiError(res.message || getGrooverText('registrationStep5PaymentEpassiStatusError'))
        return
      }
      const data = res as EpassiInitiatePaymentResponse
      submitEpassiForm(data)
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setEpassiError(error.message || getGrooverText('registrationStep5PaymentEpassiStatusError'))
    } finally {
      setEpassiSubmitting(false)
    }
  }, [paymentEnrollmentId, epassiPayForApi, isGroupedPayForBoth, paymentView, focusedEnrollmentId])

  useEffect(() => {
    // Wait until balances are computed — avoids picking Partner while group members are still loading.
    if (!(PayFor.Both in toBePaid)) return
    if (paymentView?.kind === 'grouped' && isGroupMembersLoading) return

    const currentSelectionHasBalance = activePaidFor(payFor)

    // V4 grouped: URL is for the focused enrollment — default to paying for self (Main).
    if (paymentView?.kind === 'grouped' && !enrollmentUserIdFromUrl) {
      if (
        !userSelectedPayForRef.current &&
        activePaidFor(PayFor.Main) &&
        (payFor === PayFor.Partner || payFor === PayFor.Both)
      ) {
        setPayFor(PayFor.Main)
        return
      }
      if (currentSelectionHasBalance) return
      if (activePaidFor(PayFor.Main)) {
        setPayFor(PayFor.Main)
      } else if (activePaidFor(PayFor.Partner)) {
        setPayFor(PayFor.Partner)
      } else if (activePaidFor(PayFor.Both) && !isGroupedWithoutBoth) {
        setPayFor(PayFor.Both)
      }
      return
    }

    // Legacy V3: default "pay for" to an option that has something to pay.
    // When hardCoupleIndividualParticipantFlow is enabled, never default to PayFor.Both.
    // IMPORTANT: Do not override the user's current selection if it still has a balance —
    // otherwise the receipt can be attributed to mainUser when they intended to pay for partner.
    if (currentSelectionHasBalance) return

    let newPayFor = isHardCoupleIndividualFlow || isGroupedWithoutBoth ? PayFor.Main : PayFor.Both
    if (
      !activePaidFor(PayFor.Main) ||
      !activePaidFor(PayFor.Partner) ||
      isHardCoupleIndividualFlow ||
      isGroupedWithoutBoth
    ) {
      if (!activePaidFor(PayFor.Main)) {
        newPayFor = PayFor.Partner
      } else {
        newPayFor = PayFor.Main
      }
    }
    setPayFor(newPayFor)
  }, [
    toBePaid,
    isHardCoupleIndividualFlow,
    isGroupedWithoutBoth,
    activePaidFor,
    payFor,
    paymentView,
    isGroupMembersLoading,
    enrollmentUserIdFromUrl
  ])

  // Full remaining for Swish (includes admin fee): used as max for amount input, matches backend getTotalUnpaidPaymentsAmount
  const maxSwishAmountInCents = useMemo(() => {
    if (paymentView?.kind === 'grouped' && paymentView.members.length >= 2) {
      const baseAmount = toBePaid[effectivePayFor] || 0
      const swishFee = client ? getPaymentMethodFee(PaymentMethod.Swish, client) : 0
      return baseAmount + swishFee
    }
    const source = !!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
    if (!source) return 0
    const toBePaidWithFee = getToBePaidData(source, true)
    return toBePaidWithFee[effectivePayFor] || 0
  }, [state.obfuscatedEnrollment, enrollment, effectivePayFor, paymentView, toBePaid, client])

  // Sync swishAmountInCents to max when max changes (e.g. payFor or enrollment changes)
  useEffect(() => {
    if (maxSwishAmountInCents > 0) {
      // Always reset to max when payFor changes — partial amount from a previous selection
      // should not carry over (e.g. switching from Main→Both should show 18 SEK, not 9 SEK)
      setSwishAmountInCents(maxSwishAmountInCents)
    }
  }, [maxSwishAmountInCents])

  useEffect(() => {
    if (!client) return

    // Filter payment methods based on currency-specific minimum amounts
    // https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
    let availablePaymentMethods = client.paymentMethodFees
    // get amount depending on split payment radio button selection
    let amount = toBePaid[payFor] || 0

    // Check if amount meets Stripe minimum for the current currency
    if (!meetsStripeMinimum(amount, currency)) {
      availablePaymentMethods = availablePaymentMethods.filter((pm) => pm.method !== PaymentMethod.Stripe)
    }

    // Swish is SEK-only (Swedish payment rail); remove it for non-SEK clients regardless of amount
    if (currency !== 'SEK') {
      availablePaymentMethods = availablePaymentMethods.filter((pm) => pm.method !== PaymentMethod.Swish)
    }

    if (amount <= 0) {
      availablePaymentMethods = availablePaymentMethods.filter(
        (pm) => pm.method !== PaymentMethod.Stripe && pm.method !== PaymentMethod.Swish
      )
    }

    // offer only Vipps Payments if currency is NOK; for non-NOK clients don't show Vipps Payments
    // IMPORTANT: Filter the already-filtered list, not the original client.paymentMethodFees
    // to preserve the amount-based filtering done above (Stripe minimum amount check)
    if (currency === 'NOK') {
      availablePaymentMethods = availablePaymentMethods.filter(
        (pm) => pm.method === PaymentMethod.VippsMobile || pm.method === PaymentMethod.BankTransfer
      )
    } else {
      availablePaymentMethods = availablePaymentMethods.filter((pm) => pm.method !== PaymentMethod.VippsMobile)
    }

    // Fritidskortet: Swedish government subsidy (SEK only) and only when client has the feature toggle enabled
    if (currency !== 'SEK' || !client?.featureToggles?.fritidskortetPaymentApi) {
      availablePaymentMethods = availablePaymentMethods.filter((pm) => pm.method !== PaymentMethod.Fritidskortet)
    }

    // ePassi: employee benefit payment (SEK only), requires feature toggle and positive amount
    if (!client?.featureToggles?.epassiPayment || currency !== 'SEK' || amount <= 0) {
      availablePaymentMethods = availablePaymentMethods.filter((pm) => pm.method !== PaymentMethod.Epassi)
    }

    // V4 grouped PayFor.Both: only methods with a group-aware callable can pay
    // for both enrollments in one transaction. See GROUP_PAY_FOR_BOTH_PAYMENT_METHODS.
    if (isGroupedPayForBoth) {
      availablePaymentMethods = availablePaymentMethods.filter((pm) =>
        GROUP_PAY_FOR_BOTH_PAYMENT_METHODS.has(pm.method)
      )
    }

    // If activity is configured to only allow Swish payment, filter to only show Swish (when available)
    if (activity?.onlySwishPayment === true) {
      const hasSwish = availablePaymentMethods.some((pm) => pm.method === PaymentMethod.Swish)
      if (hasSwish) {
        // Only show Swish, but keep BankTransfer if amount <= 0 (to maintain existing behavior)
        if (amount <= 0) {
          availablePaymentMethods = availablePaymentMethods.filter(
            (pm) => pm.method === PaymentMethod.Swish || pm.method === PaymentMethod.BankTransfer
          )
        } else {
          availablePaymentMethods = availablePaymentMethods.filter((pm) => pm.method === PaymentMethod.Swish)
        }
      }
      // If Swish is not available, fall back to normal behavior (don't restrict)
    }

    setEnabledPaymentMethods(availablePaymentMethods)
  }, [client, state.finalPrice, payFor, activity, currency, toBePaid, isGroupedPayForBoth])

  /**
   * Check if enrollment (or specific user when ?u= is provided) is already paid.
   * Used to prevent unnecessary Stripe payment intent creation.
   */
  const isEnrollmentAlreadyPaid = useCallback(() => {
    // When using ?u= parameter, check if this specific user has paid
    if (enrollmentUserIdFromUrl && (state.obfuscatedEnrollment || enrollment)) {
      const enrollmentToCheck = state.obfuscatedEnrollment || enrollment
      if (!enrollmentToCheck) return false

      const userStatus = getUserPaymentStatus(enrollmentToCheck, effectivePayFor)
      // User has paid if their status is 'paid' (balance <= 0)
      return userStatus.status === 'paid'
    }

    // For regular flow (no ?u=), check if entire enrollment is paid
    const sources = [
      (enrollmentPublicMonitor.data as EnrollmentPublic | undefined)?.paymentStatus,
      state.obfuscatedEnrollment?.paymentStatus,
      enrollment?.paymentStatus
    ]
    return sources.some((status) => status === EnrollmentPaymentStatus.Paid)
  }, [enrollmentPublicMonitor.data, state.obfuscatedEnrollment, enrollment, enrollmentUserIdFromUrl, effectivePayFor])

  useEffect(() => {
    // Early exit conditions
    if (state.paymentMethod !== PaymentMethod.Stripe || state.isPaid || !client) return
    if (!enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.Stripe)) return
    if (isEnrollmentAlreadyPaid()) return

    // Guard: when ?u= parameter is set, don't create a payment intent until we can resolve
    // which user (main/partner) it belongs to. Without enrollment data, effectivePayFor falls
    // through to the default logic and may send the wrong payFor value to the backend.
    if (enrollmentUserIdFromUrl) {
      const source = state.obfuscatedEnrollment || enrollment
      if (!source) {
        console.log('setupStripe: waiting for enrollment data to resolve enrollmentUserIdFromUrl')
        return
      }
      const isKnownUser =
        source.mainUser?.id === enrollmentUserIdFromUrl || source.partnerUser?.id === enrollmentUserIdFromUrl
      if (!isKnownUser) {
        console.warn('setupStripe: enrollmentUserIdFromUrl does not match any user in enrollment, aborting', {
          enrollmentUserIdFromUrl,
          mainUserId: source.mainUser?.id,
          partnerUserId: source.partnerUser?.id
        })
        return
      }
    }

    const setupStripe = async () => {
      if (!state.enrollmentId && !state.obfuscatedEnrollment?.id) return

      // Ensure we have enrollment data
      // For obfuscated enrollments, use state.obfuscatedEnrollment directly
      // For regular enrollments, use enrollment from useEnrollment hook
      let enrollmentToUse: EnrollmentWithPayment | undefined
      let needsRefetch = false

      if (state.obfuscatedEnrollment?.id) {
        // Use obfuscated enrollment data directly
        enrollmentToUse = state.obfuscatedEnrollment
      } else {
        // Regular enrollment flow - refetch if needed
        enrollmentToUse = enrollment
        const enrollmentPublicData = enrollmentPublicMonitor.data as EnrollmentPublic | undefined

        needsRefetch =
          !enrollment ||
          (enrollmentPublicData &&
            enrollment &&
            dayjs(enrollmentPublicData.updatedAt).isAfter(dayjs(enrollment.createdAt))) ||
          false

        if (needsRefetch) {
          const freshEnrollmentResult = await refetchEnrollment()
          enrollmentToUse = isGetObfuscatedEnrollmentResponse(freshEnrollmentResult.data)
            ? freshEnrollmentResult.data.enrollment
            : undefined
          if (!enrollmentToUse) {
            console.error('setupStripe: Failed to refetch enrollment data')
            void notify(`Payment error: Failed to refetch enrollment ${state.enrollmentId}`, SlackChannels.OPS_ERRORS)
            setStripeError({ hasError: true, textKey: 'stripePaymentError' })
            return
          }
        }
      }

      if (!enrollmentToUse) {
        console.error('setupStripe: No enrollment data available for payment')
        void notify(`Payment error: No enrollment data available for ${state.enrollmentId}`, SlackChannels.OPS_ERRORS)
        setStripeError({ hasError: true, textKey: 'stripePaymentError' })
        return
      }

      // Calculate amount to pay
      const currentToBePaid =
        paymentView?.kind === 'grouped' && paymentView.members.length >= 2 && focusedEnrollmentId
          ? getGroupToBePaidData(paymentView.members, focusedEnrollmentId)
          : getToBePaidData(enrollmentToUse, false)
      const amountToPay = toBePaid[effectivePayFor] || currentToBePaid[effectivePayFor] || 0

      // Validate amount - if 0, the user has already paid or has no confirmed activities
      if (amountToPay <= 0) {
        console.log('setupStripe: No payment required, amount is 0', {
          enrollmentId: state.obfuscatedEnrollment?.id || state.enrollmentId,
          payFor: effectivePayFor,
          paymentStatus: enrollmentToUse.paymentStatus,
          currentToBePaid,
          enrollmentUserIdFromUrl
        })

        // Check if user has paid using getUserPaymentStatus
        const userStatus = getUserPaymentStatus(enrollmentToUse, effectivePayFor)
        const isUserPaid = userStatus.status === 'paid'

        if (isUserPaid) {
          console.log('setupStripe: User has paid, completing registration', {
            enrollmentUserIdFromUrl,
            paymentStatus: enrollmentToUse.paymentStatus,
            userStatus
          })
          setEnrollmentPaidProcessing(true)
          onSetIsPaid(true)
          completeAfterSuccessfulPayment()
        } else {
          // No confirmed activities or pending approval
          setStripeError({ hasError: true, textKey: 'stripePaymentError' })
          toast.error(
            getGrooverText('paymentErrorNoPendingAmount') ||
              'No payment required. Activities may be pending approval or already paid.'
          )
        }
        return
      }

      // Check currency-specific minimum amount
      const minimumAmount = getStripeMinimumAmount(currency)
      if (amountToPay < minimumAmount) {
        console.error('setupStripe: Payment amount below minimum', { amountToPay, minimumAmount, currency })
        void notify(
          `Payment error: Amount ${amountToPay} (${currency}) below minimum ${minimumAmount} for enrollment ${state.obfuscatedEnrollment?.id || state.enrollmentId}`,
          SlackChannels.OPS_ERRORS
        )
        setStripeError({ hasError: true, textKey: 'stripePaymentError' })
        const minAmountFormatted = (minimumAmount / 100).toFixed(2)
        toast.error(
          getGrooverText('paymentErrorBelowMinimum') ||
            `Payment amount is below the minimum (${minAmountFormatted} ${currency}). Please contact support.`
        )
        return
      }

      // Create Stripe payment intent
      const config = await getStripeConfig(client)

      if (!config || !config.secret) {
        console.error('setupStripe: Stripe configuration missing or invalid', { clientId: client.id, config })
        void notify(
          `Payment error: Stripe config missing for client ${client.id}, enrollment ${state.obfuscatedEnrollment?.id || state.enrollmentId}`,
          SlackChannels.OPS_ERRORS
        )
        setStripeError({ hasError: true, textKey: 'stripePaymentError' })
        return
      }

      setStripeConfig(config)

      const usePayForGroup =
        paymentView?.kind === 'grouped' && effectivePayFor === PayFor.Both && client.allowPayForBoth === true

      if (usePayForGroup) {
        const groupResult = await payForGroup({
          enrollmentGroupId: paymentView.enrollmentGroupId,
          paymentMethod: PaymentMethod.Stripe
        })

        if (!groupResult.ok) {
          console.error('setupStripe: payForGroup failed', groupResult.message)
          await notify(
            `Payment error: payForGroup failed\n` +
              `Group: ${paymentView.enrollmentGroupId}\n` +
              `Error: ${groupResult.message || 'Unknown error'}\n` +
              `ErrorCode: ${groupResult.errorCode || 'none'}`,
            SlackChannels.OPS_ERRORS
          )
          setStripeError({ hasError: true, textKey: 'stripePaymentError' })
          toast.error(getGrooverText('paymentErrorGeneric'))
          return
        }

        setStripeClientSecret(groupResult.clientSecret)
        return
      }

      // Include diagnostic data for validating race condition fix
      // See: docs/STRIPE_PAYMENT_RACE_CONDITION_FIX.md
      const request: CreateStripePaymentIntentWebRequest = {
        enrollmentId: paymentEnrollmentId,
        payFor: apiPayFor,
        // Pass enrollmentUserId when using ?u= parameter (critical for hardCoupleIndividualParticipantFlow)
        // This ensures the backend knows which user is actually paying, even when the enrollment is obfuscated
        enrollmentUserId: enrollmentUserIdFromUrl,
        _diagnostics: {
          clientVersion: 'v2-race-fix',
          paymentStatusSources: {
            enrollmentPublicMonitor: (enrollmentPublicMonitor.data as EnrollmentPublic | undefined)?.paymentStatus,
            obfuscatedEnrollment: state.obfuscatedEnrollment?.paymentStatus,
            enrollment: enrollment?.paymentStatus
          },
          didRefetchEnrollment: needsRefetch ?? false,
          frontendCalculatedAmount: amountToPay
        }
      }
      const result = await createStripePaymentIntent(request)

      if ('isError' in result) {
        console.error('setupStripe: Payment intent creation failed', result.message)

        // Enhanced error notification with context for ops team
        const enrollmentId = paymentEnrollmentId
        const userInfo = enrollmentUserIdFromUrl
          ? `user ${enrollmentUserIdFromUrl}`
          : state.user?.email || state.phoneNumber || 'unknown'
        const amountInfo = `${(amountToPay / 100).toFixed(2)} ${currency}`

        void notify(
          `Payment error: Stripe intent creation failed\n` +
            `Enrollment: ${enrollmentId}\n` +
            `User: ${userInfo}\n` +
            `Amount: ${amountInfo}\n` +
            `PayFor: ${apiPayFor}\n` +
            `Error: ${result.message || 'Unknown error'}\n` +
            `ErrorCode: ${result.errorCode || 'none'}`,
          SlackChannels.OPS_ERRORS
        )
        setStripeError({ hasError: true, textKey: 'stripePaymentError' })

        // Display specific error message based on error code
        let errorMessage: string
        if (result.errorCode === 'main_user_already_paid') {
          errorMessage = getGrooverText('stripePaymentErrorMainUserAlreadyPaid')
        } else if (result.errorCode === 'partner_user_already_paid') {
          errorMessage = getGrooverText('stripePaymentErrorPartnerUserAlreadyPaid')
        } else if (result.errorCode === 'both_users_already_paid') {
          errorMessage = getGrooverText('stripePaymentErrorBothUsersAlreadyPaid')
        } else {
          errorMessage = getGrooverText('paymentErrorGeneric')
        }

        toast.error(errorMessage)
      } else {
        setStripeClientSecret(result.clientSecret)
      }
    }

    setStripeOptions(undefined)
    setupStripe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    state.enrollmentId,
    state.obfuscatedEnrollment?.id,
    state.isPaid,
    state.paymentMethod,
    effectivePayFor,
    enabledPaymentMethods,
    toBePaid,
    enrollment,
    enrollmentPublicMonitor.data,
    refetchEnrollment,
    isEnrollmentAlreadyPaid,
    currency,
    enrollmentUserIdFromUrl,
    onCompleteRegistration,
    onSetIsPaid,
    state.phoneNumber,
    state.user?.email,
    paymentView,
    paymentEnrollmentId,
    apiPayFor,
    focusedEnrollmentId
    // Note: state.obfuscatedEnrollment is intentionally not included to avoid unnecessary re-renders
    // We track state.obfuscatedEnrollment?.id which is sufficient for this effect
  ])

  useEffect(() => {
    if (!stripeClientSecret) return
    setStripeOptions({ clientSecret: stripeClientSecret })
  }, [stripeClientSecret])

  // Stripe-specific real-time signal: fire onSetIsPaid as soon as webhook confirms payment
  // Works in parallel with useEnrollmentPublic — whichever fires first wins (idempotent)
  useEffect(() => {
    if (!stripePaymentIntentStatus?.data) return
    const { status } = stripePaymentIntentStatus.data as { status: StripePaymentIntentStatusValue }
    if (status === StripePaymentIntentStatusValue.Succeeded) {
      setEnrollmentPaidProcessing(true)
      onSetIsPaid(true)
    }
  }, [stripePaymentIntentStatus?.data, onSetIsPaid])

  useEffect(() => {
    if (!client || (!state.phoneNumber && !state.obfuscatedEnrollment?.id)) return

    const fetchCredit = async () => {
      try {
        const data = await getCreditForUser({
          clientId: client.id,
          phone: state.phoneNumber,
          enrollmentId: state.obfuscatedEnrollment?.id
        })
        setUserCredit(data?.credit || 0)
      } catch (error) {
        // Gracefully handle credit fetch failure - user can still proceed with payment
        console.error('Failed to fetch user credit:', error)
        setUserCredit(0) // Default to no credit available
      }
    }

    fetchCredit()
  }, [client, state.phoneNumber, state.obfuscatedEnrollment?.id])

  const formProps = useForm<{ phoneNumber: string }>(getValidationSchema(client), {
    defaultValues: {
      phoneNumber: state.phoneNumber || ''
    },
    mode: 'all', // This enables validation on blur
    reValidateMode: 'onChange' // This re-validates on change after initial blur
  })

  // Prefill Swish phone input from step 1 main user phone.
  useEffect(() => {
    if (state.paymentMethod !== PaymentMethod.Swish) return
    if (!state.phoneNumber) return
    const current = formProps.getValues('phoneNumber')?.trim()
    if (current) return
    formProps.setValue('phoneNumber', state.phoneNumber, { shouldValidate: true })
  }, [state.paymentMethod, state.phoneNumber])

  const openSwishApp = (token?: string) => {
    if (typeof window === 'undefined') return

    const fallbackUrl = 'https://swish.nu'
    const callbackUrl = window.location.href
    const appUrl =
      token && token.length > 0
        ? `swish://paymentrequest?token=${token}&callbackurl=${encodeURIComponent(callbackUrl)}`
        : 'swish://'

    try {
      const link = document.createElement('a')
      link.href = appUrl
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (e) {
      console.error('Failed to open Swish deep link', e)
    }

    // Fallback: open Swish website in new tab after a short delay
    setTimeout(() => {
      // If the deep link succeeded, the browser is typically backgrounded (visibilityState = 'hidden').
      if (document.visibilityState !== 'visible') return
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
    }, 1000)
  }

  const onSwishPhoneSubmit = async (data: { phoneNumber: string }) => {
    setSwishError({ hasError: false })
    setIsLoading(true)

    // V4 grouped "Pay for both" routes to the group callable; the returned
    // instructionUUID is monitored by the same useSwishPaymentIntentStatus
    // listener as the single-enrollment flow.
    if (isGroupedPayForBoth && paymentView?.kind === 'grouped' && focusedEnrollmentId) {
      const groupRequest =
        swishPhoneOption === 'registered'
          ? {
              enrollmentGroupId: paymentView.enrollmentGroupId,
              payerEnrollmentId: focusedEnrollmentId,
              usePhoneFromEnrollment: true as const
            }
          : {
              enrollmentGroupId: paymentView.enrollmentGroupId,
              payerEnrollmentId: focusedEnrollmentId,
              payerPhone: getPhoneForSwish(data.phoneNumber)
            }
      const response = await payForGroupWithSwish(groupRequest)
      if (!response.ok) {
        setIsLoading(false)
        setSwishError({ hasError: true, errorCode: response.errorCode })
        return
      }
      setSwishInstructionUUID(response.instructionUUID)
      setSwishToken(response.token || '')
      setIsLoading(false)
      setSwishPaymentIntentStatus(SwishPaymentStatus.PENDING)
      if (isPhoneDevice) {
        openSwishApp(response.token)
      }
      return
    }

    const amountToSend = swishAmountInCents >= 1 ? swishAmountInCents : maxSwishAmountInCents

    const requestData: CreatePaymentInput = {
      enrollmentId: paymentEnrollmentId,
      payFor: apiPayFor,
      amountInCents: amountToSend,
      ...(swishPhoneOption === 'registered'
        ? { usePhoneFromEnrollment: true as const }
        : { payerPhone: getPhoneForSwish(data.phoneNumber) })
    }

    const response = await createSwishPaymentIntent(requestData)
    if ('isError' in response) {
      setIsLoading(false)
      setSwishError({ hasError: true, errorCode: response.errorCode })
      return
    }
    setSwishError({ hasError: false })
    setSwishInstructionUUID(response.instructionUUID)
    setSwishToken(response.token || '')
    setIsLoading(false)
    setSwishPaymentIntentStatus(SwishPaymentStatus.PENDING) // monitor swish payment status for just created payment

    // Phone-only: attempt to deep link into Swish after creating intent.
    if (isPhoneDevice) {
      openSwishApp(response.token)
    }
  }

  const onVippsMobilePhoneSubmit = async (data: { phoneNumber: string }) => {
    setVippsError({ hasError: false })
    setIsLoading(true)

    // V4 grouped "Pay for both" routes to the group callable; the returned
    // reference is monitored by the same useVippsPaymentStatus listener.
    if (isGroupedPayForBoth && paymentView?.kind === 'grouped' && focusedEnrollmentId) {
      const response = await payForGroupWithVipps({
        enrollmentGroupId: paymentView.enrollmentGroupId,
        payerEnrollmentId: focusedEnrollmentId,
        payerPhoneNumber: getPhoneForVippsMobile(data.phoneNumber)
      })
      setIsLoading(false)
      if (!response.ok) {
        return setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
      }
      setVippsPaymentReference(response.reference)
      setVippsPaymentStatus(undefined)
      return
    }

    const response = await createVippsPayment({
      enrollmentId: paymentEnrollmentId,
      payerPhoneNumber: getPhoneForVippsMobile(data.phoneNumber),
      payFor: apiPayFor
    })
    if ('isError' in response) {
      setIsLoading(false)
      return setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
    }
    setVippsPaymentReference(response.reference)
    setIsLoading(false)
    setVippsPaymentStatus(undefined) // monitor vipps payment status for just created payment
  }

  const onVippsCreateCheckoutSession = async () => {
    // Prevent concurrent calls (race condition protection)
    if (creatingVippsSessionRef.current) {
      console.log('Vipps checkout session creation already in progress, ignoring duplicate call')
      return
    }

    if (!isVippsLoaded) {
      console.error('Vipps scripts not loaded yet')
      setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
      return
    }

    setVippsError({ hasError: false })
    setIsLoading(true)
    creatingVippsSessionRef.current = true

    try {
      // V4 grouped "Pay for both" routes to the group callable, which returns
      // the checkout frontend token + URL to launch Vipps Checkout Direct.
      if (isGroupedPayForBoth && paymentView?.kind === 'grouped' && focusedEnrollmentId) {
        const response = await payForGroupWithVipps({
          enrollmentGroupId: paymentView.enrollmentGroupId,
          payerEnrollmentId: focusedEnrollmentId
        })
        if (!response.ok || !response.frontend) {
          setIsLoading(false)
          return setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
        }
        if (typeof window !== 'undefined' && window.VippsCheckoutDirect) {
          setVippsPaymentReference(response.reference)
          setVippsCheckoutSessionActive(true)
          window.VippsCheckoutDirect({
            checkoutFrontendUrl: response.frontend.checkoutFrontendUrl,
            language: getVippsLanguage(),
            token: response.frontend.token
          })
        } else {
          console.error('Vipps Checkout SDK not loaded')
          setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
        }
        setIsLoading(false)
        return
      }

      const response = await createVippsSessionCheckout({
        enrollmentId: paymentEnrollmentId,
        payFor: apiPayFor
      })

      if ('isError' in response) {
        setIsLoading(false)
        return setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
      }

      // Use Vipps Checkout Direct instead of the old approach
      if (typeof window !== 'undefined' && window.VippsCheckoutDirect) {
        // Set the reference BEFORE loading iframe to prevent double-trigger
        // This hides the "Continue with Vipps" button while iframe is active
        setVippsPaymentReference(response.reference)
        setVippsCheckoutSessionActive(true)
        window.VippsCheckoutDirect({
          checkoutFrontendUrl: response.frontend.checkoutFrontendUrl,
          language: getVippsLanguage(),
          token: response.frontend.token
        })
      } else {
        console.error('Vipps Checkout SDK not loaded')
        setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
      }

      setIsLoading(false)
    } catch (error) {
      console.error('Error creating Vipps checkout session:', error)
      setIsLoading(false)
      setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
    } finally {
      creatingVippsSessionRef.current = false
    }
  }

  const onHandleCancelSwishPayment = () => {
    cancelSwishPaymentIntent({ instructionUUID: swishInstructionUUID })
    setSwishInstructionUUID('')
    setSwishPaymentIntentStatus(SwishPaymentStatus.PENDING)
    setSwishToken('')
    formProps.setValue('phoneNumber', state.phoneNumber || '', { shouldValidate: true })
  }

  const onHandleCancelVippsPayment = () => {
    cancelVippsPayment({ reference: vippsPaymentReference })
    setVippsPaymentReference('')
    setVippsPaymentStatus(undefined)
    setVippsError({ hasError: false })
    formProps.setValue('phoneNumber', '')
  }

  useEffect(() => {
    if (!swishPaymentIntentMonitor) return
    if (swishPaymentIntentMonitor.status == 'success') {
      // db listening is not error
      // @ts-ignore
      const { status, errorCode } = swishPaymentIntentMonitor.data // get payment status data
      if (status) {
        setSwishPaymentIntentStatus(status)
      }
      if (errorCode) {
        setSwishError((prev) => ({ ...prev, errorCode }))
      }
    }
  }, [swishPaymentIntentMonitor.data])

  const swishPaidHandledRef = useRef(false)

  useEffect(() => {
    if (swishPaymentIntentStatus == SwishPaymentStatus.PAID) {
      setEnrollmentPaidProcessing(true)
      if (swishPaidHandledRef.current) return
      swishPaidHandledRef.current = true

      const handlePaid = async () => {
        // Wait for webhook to complete and enrollment to update
        await new Promise((resolve) => setTimeout(resolve, 1500))

        const enrollmentId = state.obfuscatedEnrollment?.id || state.enrollmentId
        if (!enrollmentId) {
          onSetIsPaid(true)
          completeAfterSuccessfulPayment()
          return
        }

        let freshEnrollment: EnrollmentWithPayment | undefined
        if (state.obfuscatedEnrollment?.id) {
          const result = await getObfuscatedEnrollment({
            enrollmentId: state.obfuscatedEnrollment.id,
            enrollmentUserId: enrollmentUserIdFromUrl
          })
          if (result && !('isError' in result)) {
            freshEnrollment = result.enrollment
            onSetObfuscatedEnrollment(result.enrollment)
          }
        } else {
          const result = await refetchEnrollment()
          freshEnrollment = isGetObfuscatedEnrollmentResponse(result.data) ? result.data.enrollment : undefined
        }

        if (!freshEnrollment) {
          onSetIsPaid(true)
          completeAfterSuccessfulPayment()
          return
        }

        const remainingData = getToBePaidData(freshEnrollment, true)
        const remaining = remainingData[effectivePayFor] || 0

        if (remaining > 0) {
          toast.success(
            formatGrooverText(getGrooverText('registrationStep5PaymentSwishPartialSuccess'), [
              (remaining / 100).toFixed(0).replace('.00', ''),
              currency
            ])
          )
          setEnrollmentPaidProcessing(false)
          setSwishInstructionUUID('')
          setSwishToken('')
          setSwishPaymentIntentStatus(SwishPaymentStatus.PENDING)
          swishPaidHandledRef.current = false
        } else {
          onSetIsPaid(true)
          completeAfterSuccessfulPayment()
        }
      }

      handlePaid()
    }
    if (swishPaymentIntentStatus == SwishPaymentStatus.ERROR) {
      setEnrollmentPaidProcessing(false)
      setSwishInstructionUUID('') // this will allow us to come back to state where user need to enter the phone number
      setSwishError((prev) => ({ ...prev, hasError: true }))
    }
    if (
      [SwishPaymentStatus.CANCELLED, SwishPaymentStatus.DECLINED].includes(
        swishPaymentIntentStatus as SwishPaymentStatus
      )
    ) {
      setEnrollmentPaidProcessing(false)
      setSwishInstructionUUID('') // this will allow us to come back to state where user need to enter the phone number
    }
  }, [
    swishPaymentIntentStatus,
    state.obfuscatedEnrollment,
    state.enrollmentId,
    effectivePayFor,
    currency,
    enrollmentUserIdFromUrl,
    onSetIsPaid,
    onCompleteRegistration,
    onSetObfuscatedEnrollment,
    refetchEnrollment
  ])

  useEffect(() => {
    if (!vippsPaymentIntentMonitor) return
    if (vippsPaymentIntentMonitor.status == 'success') {
      const data = vippsPaymentIntentMonitor.data as VippsPaymentStatus | VippsPaymentStatusHollowInit
      if (data.name && data.name !== 'INITIALIZED') {
        setVippsPaymentStatus(data)
      }
    }
  }, [vippsPaymentIntentMonitor.data])

  useEffect(() => {
    if (!vippsPaymentStatus) return
    if (!vippsPaymentStatus.success) {
      console.log(`vipps payment error`, vippsPaymentStatus)
      setEnrollmentPaidProcessing(false)
      setVippsPaymentReference('') // this will allow us to come back to state where user need to enter the phone number
      setVippsCheckoutSessionActive(false)
      setVippsError({ hasError: true, textKey: 'vippsPaymentError' })
    }
    if (vippsPaymentStatus.success) {
      if (['CAPTURED', 'AUTHORIZED'].includes(vippsPaymentStatus.name as string)) {
        console.log(`vipps payment is ${vippsPaymentStatus}, will move forward to next step`)
        setEnrollmentPaidProcessing(true)
        onSetIsPaid(true)
        completeAfterSuccessfulPayment()
      }
      if (['ABORTED', 'EXPIRED', 'CANCELLED', 'TERMINATED'].includes(vippsPaymentStatus.name as string)) {
        setEnrollmentPaidProcessing(false)
        setVippsPaymentReference('') // this will allow us to come back to state where user need to enter the phone number
        setVippsCheckoutSessionActive(false)
      }
    }
  }, [vippsPaymentStatus, completeAfterSuccessfulPayment])

  const { hasPartner } = useDiscountCalculations({
    activity,
    client,
    state
  })

  const onSelectPaymentMethod = (paymentMethod: PaymentMethod) => {
    if (paymentMethod === PaymentMethod.Fritidskortet && fritidskortetAlreadyUsedForEnrollment) return
    if (!state.isPaid) {
      if (state.paymentMethod !== paymentMethod) {
        // Clear all payment method errors when switching
        setSwishError({ hasError: false })
        setVippsError({ hasError: false })
        setStripeError({ hasError: false })
        setCreditError({ hasError: false })
      }
      return onSetPaymentMethod(paymentMethod)
    }
  }

  const onPayWithUserCredit = async () => {
    setCreditError({ hasError: false }) // Clear any previous errors
    setIsLoading(true)
    if (client && !state.isPaid && userCredit > 0) {
      const amountDue = toBePaid[effectivePayFor] || 0
      const payWithCredit = async (): Promise<boolean> => {
        if (
          paymentView?.kind === 'grouped' &&
          effectivePayFor === PayFor.Both &&
          paymentView.showPayForGroup &&
          focusedEnrollmentId
        ) {
          const groupResult = await payForGroupWithUserCredit({
            enrollmentGroupId: paymentView.enrollmentGroupId,
            payerEnrollmentId: focusedEnrollmentId
          })
          return groupResult.ok
        }

        const request = {
          phone: state.phoneNumber,
          clientId: client.id,
          enrollmentId: paymentEnrollmentId,
          amount: Math.min(userCredit, amountDue),
          ...(paymentView?.kind === 'grouped' &&
            effectivePayFor === PayFor.Partner && {
              payerEnrollmentId: focusedEnrollmentId
            })
        }

        const result = await payWithUserCredit(request)
        return result
      }

      try {
        const result = await payWithCredit()

        if (result) {
          // Show success notification
          toast.success(getGrooverText('creditPaymentSuccessNotification'))

          if (userCredit >= amountDue) {
            onNextStep() // fully paid with credit, move to next step
          } else {
            onSetPaymentMethod(PaymentMethod.BankTransfer) // if credit is not enough to cover full amount
            setUserCredit(0) // set credit to 0 as we used it all
            // reload enrollment data and update state - this will trigger all dependent useEffect hooks
            // If we have enrollmentId, refetch will work; if we only have obfuscatedEnrollment, need to fetch explicitly
            if (state.enrollmentId) {
              await refetchEnrollment()
              // enrollment variable will be automatically updated by react-query
              // useEffect will trigger due to enrollment dependency change (toBePaid etc)
            } else if (state.obfuscatedEnrollment?.id) {
              // Fetch fresh data for obfuscated enrollment
              const freshEnrollmentResponse = await getObfuscatedEnrollment({
                enrollmentId: state.obfuscatedEnrollment.id,
                enrollmentUserId: enrollmentUserIdFromUrl
              })
              if (freshEnrollmentResponse && !('isError' in freshEnrollmentResponse)) {
                onSetObfuscatedEnrollment(freshEnrollmentResponse.enrollment)
                // useEffect will trigger due to state.obfuscatedEnrollment dependency change (toBePaid etc)
              }
            }
          }
        } else {
          setCreditError({ hasError: true, textKey: 'creditPaymentError' })
        }
      } catch (error) {
        console.error('Error in credit payment:', error)
        setCreditError({ hasError: true, textKey: 'creditPaymentError' })
      }
    } else {
      setCreditError({ hasError: true, textKey: 'creditPaymentError' })
    }
    setIsLoading(false)
  }

  // Get fee for a specific payment method (only when there is an amount to pay)
  const getPaymentMethodFeeForMethod = (method: PaymentMethod): number => {
    if (!client) return 0
    const totalFinalPriceOre = toBePaid[payFor] ?? 0
    return resolveAdministrationFeeAmount(method, client, totalFinalPriceOre)
  }

  // Get fee for the currently selected payment method
  const getSelectedPaymentMethodFee = (): number => {
    if (!client || !state.paymentMethod) return 0
    const totalFinalPriceOre = toBePaid[payFor] ?? 0
    return resolveAdministrationFeeAmount(state.paymentMethod, client, totalFinalPriceOre)
  }

  // Format amount helper
  const formatAmount = (amount: number) => (amount / 100).toFixed(2).toString().replace('.00', '')

  const formatToBePaid = (p: PayForEnum, isTotal?: boolean, userCredit?: number) => {
    const basePrice = 0 // Not used when toBePaid is available

    const source = !!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
    const enrollmentPriceDetails = source?.priceDetails?.map((pd) => ({
      appliesTo: pd.appliesTo,
      administrationFee: pd.administrationFee,
      finalPrice: pd.finalPrice
    }))

    return formatToBePaidHelper({
      payFor,
      p: p as PayFor,
      isTotal,
      userCredit,
      paymentMethod: state.paymentMethod,
      client: client || undefined,
      basePrice,
      toBePaid,
      enrollmentPriceDetails
    })
  }

  const payForBothOptionLabel = getGrooverText('registrationStep5PaymentPayForBothOption')

  const groupedMainMember =
    paymentView?.kind === 'grouped'
      ? paymentView.members.find((m) => m.enrollmentId === focusedEnrollmentId)
      : undefined
  const groupedPartnerMember =
    paymentView?.kind === 'grouped'
      ? paymentView.members.find((m) => m.enrollmentId !== focusedEnrollmentId)
      : undefined

  const showPayForRadios =
    !enrollmentUserIdFromUrl && (hasPartner || (paymentView?.kind === 'grouped' && paymentView.members.length >= 2))

  const showPartnerBreakdownRow = hasPartner || (paymentView?.kind === 'grouped' && showPayForRadios)

  const mainUserName =
    groupedMainMember?.displayName ??
    (!!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment?.mainUser.name : state.name)
  const partnerUserName =
    groupedPartnerMember?.displayName ??
    (!!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment?.partnerUser?.name : state?.partnerName)

  return registerActiveStep !== 4 ? (
    <ClosedContainer
      onClick={
        state.isRegistrationCompleted ||
        (!!state.obfuscatedEnrollment?.mainUser &&
          [EnrollmentPaymentStatus.Paid, EnrollmentPaymentStatus.PartlyPaid].includes(state.paymentStatus))
          ? onOpenStepFour
          : undefined
      }
    >
      <Box fullWidth direction="row" align="center" gap="15px">
        {state.isRegistrationCompleted || (!!state.obfuscatedEnrollment?.mainUser && state.isPaid) ? (
          <img src={iconCompleted} width={24} height={24} />
        ) : null}
        <GrooverText textKey="registrationHeadersAndStatusHeaderPrefPayment" />
      </Box>
    </ClosedContainer>
  ) : (
    <OpenContainer>
      <GrooverText textKey="registrationHeadersAndStatusHeaderPrefPayment" />
      {state.isPaid ? (
        <SuccessMessage fullWidth align="center" fullPadding spacing={SIXTEEN_PX}>
          <GrooverText textKey="registrationStep5PaymentPaymentCompleteStatus" />
        </SuccessMessage>
      ) : null}
      {!state.isPaid && hasPendingPositiveAmount && !hasConfirmedAmountDue ? (
        <CourseRegistrationApprovalWaiting />
      ) : null}
      {!state.isPaid && (hasConfirmedAmountDue || !hasPendingPositiveAmount) ? (
        <div style={{ position: 'relative', width: '100%' }}>
          {hasPendingPositiveAmount && hasConfirmedAmountDue ? <PartlyApprovedPaymentBanner /> : null}
          {enrollmentPaidProcessing ? (
            <PaidProcessingOverlay>
              <CircularProgress size={40} />
              <GrooverText textKey="registrationStep4PaymentPaidProcessing" style={{ fontWeight: 700 }} />
              {paidProcessingShowCancel ? (
                <div
                  onClick={() => setEnrollmentPaidProcessing(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    color: '#999',
                    fontSize: '12px'
                  }}
                >
                  <CloseIcon style={{ fontSize: 14, color: '#e53935' }} />
                  <GrooverText
                    textKey="registrationStep4PaymentPaidProcessingNetworkIssues"
                    style={{ textDecoration: 'underline', fontSize: '12px' }}
                  />
                </div>
              ) : null}
            </PaidProcessingOverlay>
          ) : null}
          <GrayBox fullWidth fullPadding spacing={EIGHT_PX}>
            {isEnrollmentPriceError ? (
              <Box fullWidth direction="column" gap={EIGHT_PX}>
                <GrooverText textKey="registrationStep4PriceLoadError" style={{ color: '#e53935' }} />
                <ContinueButton onClick={() => refetchEnrollment()}>
                  <GrooverText textKey="registrationStep4PriceLoadRetry" />
                </ContinueButton>
              </Box>
            ) : (
              <>
                {isPriceLoading || isGroupMembersLoading ? (
                  <Box fullWidth bottom spacing={SIX_PX}>
                    <GrooverText textKey="registrationStep4LoadingPrice" />
                  </Box>
                ) : null}
                <Box fullWidth direction="row" align="center" justify="space-between" bottom spacing={SIXTEEN_PX}>
                  <GrooverText textKey="componentsPricingControlPaymentTotalRow" />
                  {isPriceLoading || isGroupMembersLoading ? (
                    <SkeletonCustom>
                      <span>{'0000 ' + currency}</span>
                    </SkeletonCustom>
                  ) : (
                    <span>{formatToBePaid(payFor as PayForEnum, true) + ' ' + currency}</span>
                  )}
                </Box>

                {(payFor === PayFor.Both || payFor == PayFor.Main) && (
                  <Box fullWidth direction="row" align="center" justify="space-between" bottom spacing={SIX_PX}>
                    <span>{mainUserName}</span>
                    {isPriceLoading || isGroupMembersLoading ? (
                      <SkeletonCustom>
                        <span>{'0000 ' + currency}</span>
                      </SkeletonCustom>
                    ) : (
                      <span>{formatToBePaid(PayFor.Main as PayForEnum) + ' ' + currency}</span>
                    )}
                  </Box>
                )}
                {showPartnerBreakdownRow && (payFor === PayFor.Both || payFor == PayFor.Partner) && (
                  <Box fullWidth direction="row" align="center" justify="space-between" bottom spacing={SIX_PX}>
                    <span>{partnerUserName}</span>
                    {isPriceLoading || isGroupMembersLoading ? (
                      <SkeletonCustom>
                        <span>{'0000 ' + currency}</span>
                      </SkeletonCustom>
                    ) : (
                      <span>{formatToBePaid(PayFor.Partner as PayForEnum) + ' ' + currency}</span>
                    )}
                  </Box>
                )}
                {/* Show payment method fee for currently selected payment method */}
                {!isPaymentPriceBlocked &&
                  !isGroupMembersLoading &&
                  state.paymentMethod &&
                  getSelectedPaymentMethodFee() > 0 && (
                    <Box fullWidth direction="row" align="center" justify="space-between" top spacing={SIX_PX}>
                      <GrooverText textKey="componentsPricingControlAdministrationFeeLabel" />
                      <span>{formatAmount(getSelectedPaymentMethodFee()) + ' ' + currency}</span>
                    </Box>
                  )}
              </>
            )}
          </GrayBox>

          {showPayForRadios && (
            <Box fullWidth fullPadding spacing={EIGHT_PX}>
              <Box fullWidth direction="column" align="center" justify="space-between" bottom spacing={SIXTEEN_PX}>
                <RadioGroup row>
                  {/* Hide "Pay for Both" when hardCoupleIndividualParticipantFlow or V4 group without allowPayForBoth */}
                  {!isHardCoupleIndividualFlow &&
                    !(paymentView?.kind === 'grouped' && !paymentView.showPayForGroup) && (
                      <FormControlLabel
                        value={PayFor.Both}
                        checked={payFor === PayFor.Both}
                        disabled={!activePaidFor(PayFor.Main) || !activePaidFor(PayFor.Partner)}
                        onChange={() => onSelectPayFor(PayFor.Both)}
                        control={<Radio />}
                        label={payForBothOptionLabel}
                      />
                    )}
                  <FormControlLabel
                    value={PayFor.Main}
                    checked={payFor === PayFor.Main}
                    disabled={!activePaidFor(PayFor.Main)}
                    onChange={() => onSelectPayFor(PayFor.Main)}
                    control={<Radio />}
                    label={mainUserName}
                  />
                  <FormControlLabel
                    value={PayFor.Partner}
                    checked={payFor === PayFor.Partner}
                    disabled={!activePaidFor(PayFor.Partner)}
                    onChange={() => onSelectPayFor(PayFor.Partner)}
                    control={<Radio />}
                    label={partnerUserName}
                  />
                </RadioGroup>
              </Box>
            </Box>
          )}

          <Box fullWidth top spacing={EIGHT_PX}>
            <Box direction="row" align="center" justify="space-between" fullWidth>
              <GrooverText textKey="registrationStep5PaymentPaymentOptionHeader" />
              {/* JSON Viewer Button (development only) */}
              {isDev && (
                <Tooltip title="Show Obfuscated Enrollment JSON" arrow enterDelay={300}>
                  <IconButton
                    onClick={() => {
                      const source = !!state.obfuscatedEnrollment?.id ? state.obfuscatedEnrollment : enrollment
                      setJsonModalData({
                        obfuscatedEnrollment: state.obfuscatedEnrollment,
                        enrollment: enrollment,
                        source: source,
                        toBePaid: toBePaid,
                        payments: source?.payments,
                        priceDetails: source?.priceDetails,
                        calculatedToBePaid: getToBePaidData(source || ({} as EnrollmentWithPayment))
                      })
                      setJsonModalOpen(true)
                    }}
                    sx={{
                      color: '#666',
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <JsonIcon size={18} color="#666" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {isPriceLoading ? (
              <>
                {[0, 1, 2].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    height={48}
                    animation="wave"
                    sx={{ borderRadius: 1, marginTop: '8px' }}
                  />
                ))}
              </>
            ) : null}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.BankTransfer) &&
              (state?.paymentMethod !== PaymentMethod.BankTransfer ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.BankTransfer)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.BankTransfer} />
                    <GrooverText textKey="registrationStep5PaymentRegPaymentBank" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.BankTransfer) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.BankTransfer))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={bankIcon} width={20} />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.BankTransfer)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.BankTransfer} />
                      <GrooverText textKey="registrationStep5PaymentRegPaymentBank" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.BankTransfer) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.BankTransfer))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={bankIcon} width={20} />
                    </Box>
                  </HeaderBox>
                  <Box
                    fullWidth
                    direction={isMobile ? 'column' : 'row'}
                    align={isMobile ? 'stretch' : 'center'}
                    justify="space-between"
                    top
                    bottom
                    left
                    right
                    gap={EIGHT_PX}
                  >
                    <Box fullWidth>
                      <Box
                        fullWidth
                        direction="row"
                        align="center"
                        gap={TWO_PX}
                        style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
                      >
                        <span>
                          {formatGrooverText(getGrooverText('registrationStep5PaymentPayInfoBankAccountNewAmount'), [
                            formatToBePaid(payFor as PayForEnum, true)
                          ])}
                        </span>
                        <span>
                          {formatGrooverText(
                            getGrooverText('registrationStep5PaymentPayInfoBankAccountNewAccountNumber'),
                            [client?.bankGiro]
                          )}
                        </span>
                        <span>
                          {formatGrooverText(getGrooverText('registrationStep5PaymentPayInfoBankAccountNewRef'), [
                            state?.obfuscatedEnrollment?.ocr ? state?.obfuscatedEnrollment?.ocr : enrollment?.ocr
                          ])}
                        </span>
                      </Box>
                    </Box>
                    {isMobile ? (
                      <Box fullWidth direction="row" justify="flex-end">
                        <ContinueButton onClick={onHandleNext}>
                          <GrooverText textKey="registrationCtactaContinue" />
                          <img src={iconArrow} width={16} height={16} />
                        </ContinueButton>
                      </Box>
                    ) : (
                      <ContinueButton onClick={onHandleNext}>
                        <GrooverText textKey="registrationCtactaContinue" />
                        <img src={iconArrow} width={16} height={16} />
                      </ContinueButton>
                    )}
                  </Box>
                </PurpleContainer>
              ))}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.UserCredit) &&
              userCredit > 0 &&
              (state?.paymentMethod !== PaymentMethod.UserCredit ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.UserCredit)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.UserCredit} />
                    <GrooverText textKey="adminGeneralEnumsPaymentPaymentCreditAmount" variant="body.regular" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.UserCredit) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.UserCredit))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={iconCompleted} width={20} />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.UserCredit)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.UserCredit} />
                      <GrooverText textKey="adminGeneralEnumsPaymentPaymentCreditAmount" variant="body.regular" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.UserCredit) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.UserCredit))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={iconCompleted} width={20} />
                    </Box>
                  </HeaderBox>
                  <Box
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    top
                    bottom
                    left
                    right
                    gap={EIGHT_PX}
                  >
                    <Box>
                      <Box direction="row" align="center" gap={TWO_PX}>
                        <span style={getGrooverTextStyle('regular-dark')}>
                          {formatGrooverText(getGrooverText('registrationStep5PaymentPayInfoBankAccountNewAmount'), [
                            formatToBePaid(payFor as PayForEnum, true, userCredit)
                          ])}
                        </span>
                        <GrooverText textKey="registrationStep5PaymentPaymentCreditInfo" variant="regular-dark" />
                      </Box>
                    </Box>
                    <ContinueButton onClick={onPayWithUserCredit} isDisabled={isLoading}>
                      <GrooverText textKey="registrationCtactaContinue" />
                      <img src={iconArrow} width={16} height={16} />
                    </ContinueButton>
                  </Box>
                  {creditError.hasError && (
                    <Box fullWidth fullPadding align="center" gap={TWENTY_FOUR_PX}>
                      <RedBox fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey={creditError.textKey || 'creditPaymentError'} />
                      </RedBox>
                    </Box>
                  )}
                </PurpleContainer>
              ))}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.Swish) &&
              (state?.paymentMethod !== PaymentMethod.Swish ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.Swish)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.Swish} />
                    <GrooverText textKey="registrationStep5PaymentRegPaymentSwish" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.Swish) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Swish))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={swishIcon} width="20px" />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.Swish)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.Swish} />
                      <GrooverText textKey="registrationStep5PaymentRegPaymentSwish" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.Swish) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Swish))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={swishIcon} />
                    </Box>
                  </HeaderBox>
                  {!swishInstructionUUID && !isLoading && (
                    <Box fullWidth top bottom left right>
                      <GrooverText textKey="registrationStep5PaymentStripePaymentInfo" />

                      {/* Show radio buttons only for obfuscated enrollments */}
                      {state.obfuscatedEnrollment?.mainUser?.phone && (
                        <Box top spacing={EIGHT_PX} direction="column" gap={EIGHT_PX}>
                          <RadioGroup
                            value={swishPhoneOption}
                            onChange={(e) => setSwishPhoneOption(e.target.value as 'registered' | 'custom')}
                          >
                            <FormControlLabel
                              value="registered"
                              control={<Radio />}
                              label={formatGrooverText(
                                getGrooverText('registrationStep5PaymentSwishUseRegisteredPhone'),
                                [state.obfuscatedEnrollment.mainUser.phone]
                              )}
                            />
                            <FormControlLabel
                              value="custom"
                              control={<Radio />}
                              label={getGrooverText('registrationStep5PaymentSwishUseCustomPhone')}
                            />
                          </RadioGroup>
                        </Box>
                      )}

                      {/* Show phone input only when custom option is selected or no obfuscated enrollment */}
                      {(!state.obfuscatedEnrollment?.mainUser?.phone || swishPhoneOption === 'custom') && (
                        <Controller
                          control={formProps.control}
                          name="phoneNumber"
                          render={({ field, fieldState }) => (
                            <TextFieldWithLabelForm
                              {...field}
                              value={field.value}
                              fullWidth
                              fieldState={fieldState}
                              placeholder={getGrooverText('registrationStep5PaymentStripeEnterPhone')}
                              autoComplete="off"
                              data-form-type="other"
                              data-lpignore="true"
                            />
                          )}
                        />
                      )}

                      {/* Amount to pay — TextField only when client.registerFlowEditableSwishAmount; otherwise plain text */}
                      {maxSwishAmountInCents > 0 && (
                        <Box top spacing={EIGHT_PX}>
                          <Box
                            fullWidth
                            direction="row"
                            changeDirectionOnMobile
                            gap={FOUR_PX}
                            align="center"
                            justify="space-between"
                          >
                            <GrooverText
                              textKey="registrationStep5PaymentSwishAmount"
                              style={{ whiteSpace: 'nowrap', marginRight: EIGHT_PX }}
                            />
                            {isRegisterFlowSwishAmountEditable ? (
                              <TextField
                                fullWidth
                                type="number"
                                value={swishAmountInCents / 100}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value)
                                  if (!Number.isNaN(v)) {
                                    const cents = Math.round(v * 100)
                                    setSwishAmountInCents(Math.max(1, Math.min(cents, maxSwishAmountInCents)))
                                  }
                                }}
                                slotProps={{
                                  htmlInput: {
                                    min: 0.01,
                                    max: maxSwishAmountInCents / 100,
                                    step: 0.01,
                                    inputMode: 'decimal'
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(99, 20, 215, 0.05)',
                                    borderRadius: '12px',
                                    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' }
                                  },
                                  '& input': {
                                    textAlign: 'right',
                                    color: '#000000'
                                  }
                                }}
                              />
                            ) : (
                              <Box direction="row" gap={FOUR_PX} align="center">
                                <span
                                  style={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: '#000000',
                                    lineHeight: 1.5,
                                    fontVariantNumeric: 'tabular-nums'
                                  }}
                                >
                                  {(swishAmountInCents / 100).toLocaleString(
                                    currentLanguage === 'sv' ? 'sv-SE' : currentLanguage === 'no' ? 'nb-NO' : 'en-GB',
                                    { minimumFractionDigits: 0, maximumFractionDigits: 2 }
                                  )}
                                </span>
                                <span style={{ color: '#000000' }}>{currency}</span>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                      <Box top spacing={EIGHT_PX}>
                        <ContinueButton
                          onClick={
                            swishPhoneOption === 'registered'
                              ? () => onSwishPhoneSubmit({ phoneNumber: '' })
                              : formProps.handleSubmit(onSwishPhoneSubmit)
                          }
                          isDisabled={isLoading || (swishPhoneOption === 'custom' && !formProps.formState.isValid)}
                        >
                          <GrooverText textKey="registrationCtactaContinue" />
                          <img src={iconArrow} width={16} height={16} />
                        </ContinueButton>
                      </Box>
                    </Box>
                  )}
                  {isLoading && (
                    <Box fullWidth fullPadding align="center" gap={TWENTY_FOUR_PX}>
                      <img src={swishBigLogo} width={205} height={65} />
                      <GreenBox fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey="registrationStep5PaymentSwishPreparingPayment" />
                      </GreenBox>
                    </Box>
                  )}
                  {!isLoading && swishInstructionUUID && (
                    <Box fullWidth left right>
                      <Box fullWidth align="center" top bottom>
                        <img src={swishBigLogo} width={205} height={65} />
                      </Box>
                      <Header fullWidth fullPadding spacing={EIGHT_PX}>
                        <span>
                          {formatGrooverText(getGrooverText('registrationStep5PaymentSwishWaiting'), [
                            formProps.getValues('phoneNumber')
                          ])}
                        </span>
                      </Header>
                      <InformationContainer fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey="registrationStep5PaymentSwishWaitingInfo1" />
                        <GrooverText textKey="registrationStep5PaymentSwishWaitingInfo2" />
                        <GrooverText textKey="registrationStep5PaymentSwishWaitingInfo3" />
                        {isPhoneDevice && (
                          <Box top spacing={EIGHT_PX}>
                            <ContinueButton onClick={() => openSwishApp(swishToken)}>
                              <GrooverText textKey="registrationStep5PaymentOpenSwishButton" />
                            </ContinueButton>
                          </Box>
                        )}
                      </InformationContainer>
                      <Box bottom>
                        <AbortButton isDisabled={state.isRegistrationCompleted} onClick={onHandleCancelSwishPayment}>
                          <img src={iconArrowLeft} width={16} height={16} />
                          <GrooverText textKey="registrationCtactaAbort" />
                        </AbortButton>
                      </Box>
                    </Box>
                  )}
                  {swishError.hasError && (
                    <Box fullWidth fullPadding align="center" gap={TWENTY_FOUR_PX}>
                      <img src={swishBigLogo} width={205} height={65} />
                      <RedBox fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey={getSwishErrorTextKey(swishError.errorCode)} />
                        {swishError.errorCode && !isKnownSwishErrorCode(swishError.errorCode) && (
                          <span style={{ display: 'block', fontSize: '12px', marginTop: '4px', opacity: 0.7 }}>
                            ({swishError.errorCode})
                          </span>
                        )}
                      </RedBox>
                    </Box>
                  )}
                </PurpleContainer>
              ))}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.VippsMobile) &&
              (state?.paymentMethod !== PaymentMethod.VippsMobile ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.VippsMobile)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.VippsMobile} />
                    <GrooverText textKey="registrationStep5PaymentRegPaymentVipps" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.VippsMobile) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.VippsMobile))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={vippsMobilePaySymbol} height="24px" style={{ width: 'auto' }} />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.VippsMobile)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.VippsMobile} />
                      <GrooverText textKey="registrationStep5PaymentRegPaymentVipps" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.VippsMobile) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.VippsMobile))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={vippsMobilePaySymbol} height="24px" style={{ width: 'auto' }} />
                    </Box>
                  </HeaderBox>
                  {!vippsPaymentReference && !isLoading && !vippsCheckoutSessionActive && (
                    <Box fullWidth top bottom left right>
                      {isVippsLoading && (
                        <Box fullWidth fullPadding align="center" gap={TWELVE_PX}>
                          <GrooverText textKey="registrationStep5PaymentVippsPreparingPayment" />
                        </Box>
                      )}
                      {vippsLoadError && (
                        <RedBox fullWidth fullPadding spacing={EIGHT_PX}>
                          <GrooverText textKey="registrationStep5PaymentVippsGenericError" />
                          <span style={{ display: 'block', fontSize: '12px', marginTop: '6px', opacity: 0.85 }}>
                            {vippsLoadError}
                          </span>
                        </RedBox>
                      )}
                      {!isVippsLoading && !vippsError.hasError && !vippsLoadError && (
                        <>
                          {client?.vipps?.integrationType === 'checkout' &&
                            (() => {
                              const vippsLang = getVippsLanguage()
                              // Include source language and remount key to force complete DOM re-creation
                              const buttonKey = `vipps-button-${currentLanguage}-${vippsLang}-${vippsRemountKey}`

                              return (
                                <Box top spacing={EIGHT_PX}>
                                  <div
                                    style={{
                                      opacity: !isVippsLoaded || isLoading ? 0.5 : 1,
                                      pointerEvents: !isVippsLoaded || isLoading ? 'none' : 'auto',
                                      cursor: !isVippsLoaded || isLoading ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    {createElement('vipps-mobilepay-button', {
                                      key: buttonKey,
                                      id: 'vipps-checkout-button',
                                      variant: 'full-width',
                                      language: vippsLang,
                                      onClick: (e: any) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (!isVippsLoaded || isLoading) return
                                        onVippsCreateCheckoutSession()
                                      }
                                    })}
                                  </div>
                                </Box>
                              )
                            })()}
                          {client?.vipps?.integrationType === 'epayment' && (
                            <>
                              <GrooverText textKey="registrationStep5PaymentVippsPaymentInfo" />
                              <Controller
                                control={formProps.control}
                                name="phoneNumber"
                                render={({ field, fieldState }) => (
                                  <TextFieldWithLabelForm
                                    {...field}
                                    value={field.value}
                                    fullWidth
                                    fieldState={fieldState}
                                    placeholder={getGrooverText('registrationStep5PaymentVippsEnterPhone')}
                                  />
                                )}
                              />
                              <Box top spacing={EIGHT_PX}>
                                <ContinueButton
                                  onClick={formProps.handleSubmit(onVippsMobilePhoneSubmit)}
                                  isDisabled={!formProps.formState.isValid || isLoading}
                                >
                                  <GrooverText textKey="registrationCtactaContinue" />
                                  <img src={iconArrow} width={16} height={16} />
                                </ContinueButton>
                              </Box>
                            </>
                          )}
                        </>
                      )}
                    </Box>
                  )}
                  {isLoading && (
                    <Box fullWidth fullPadding align="center" gap={TWENTY_FOUR_PX}>
                      <img src={vippsMobilePayHorizontal} height={65} style={{ width: 'auto' }} />
                      <GreenBox fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey="registrationStep5PaymentVippsPreparingPayment" />
                      </GreenBox>
                    </Box>
                  )}
                  {!isLoading && vippsPaymentReference && (
                    <Box fullWidth left right>
                      <Box fullWidth align="center" top bottom>
                        <img src={vippsMobilePayHorizontal} height={65} style={{ width: 'auto' }} />
                      </Box>
                      <Header fullWidth fullPadding spacing={EIGHT_PX}>
                        <span>
                          {formatGrooverText(getGrooverText('registrationStep5PaymentVippsWaiting'), [
                            formProps.getValues('phoneNumber')
                          ])}
                        </span>
                      </Header>
                      <InformationContainer fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey="registrationStep5PaymentVippsWaitingInfo1" />
                        <GrooverText textKey="registrationStep5PaymentVippsWaitingInfo2" />
                        <GrooverText textKey="registrationStep5PaymentVippsWaitingInfo3" />
                      </InformationContainer>
                      <Box bottom>
                        <AbortButton isDisabled={state.isRegistrationCompleted} onClick={onHandleCancelVippsPayment}>
                          <img src={iconArrowLeft} width={16} height={16} />
                          <GrooverText textKey="registrationCtactaAbort" />
                        </AbortButton>
                      </Box>
                    </Box>
                  )}
                  {vippsError.hasError && (
                    <Box fullWidth fullPadding align="center" gap={TWENTY_FOUR_PX}>
                      <img src={vippsMobilePayHorizontal} height={65} style={{ width: 'auto' }} />
                      <RedBox fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey={vippsError.textKey || 'registrationStep5PaymentVippsGenericError'} />
                      </RedBox>
                    </Box>
                  )}
                </PurpleContainer>
              ))}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.Stripe) &&
              (state?.paymentMethod !== PaymentMethod.Stripe ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.Stripe)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.Stripe} />
                    <GrooverText textKey="registrationStep5PaymentRegPaymentCard" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.Stripe) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Stripe))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={cards} width={50} />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.Stripe)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.Stripe} />
                      <GrooverText textKey="registrationStep5PaymentRegPaymentCard" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.Stripe) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Stripe))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={cards} width={60} />
                    </Box>
                  </HeaderBox>
                  {stripeConfig && stripeOptions && !isLoading && (
                    <Box fullWidth top bottom left right>
                      <Elements stripe={getStripe(stripeConfig)} options={stripeOptions}>
                        <StripeCheckoutForm onIsPaid={onSetIsPaid} />
                      </Elements>
                    </Box>
                  )}
                  {(isLoading || !stripeConfig || !stripeOptions) && (
                    <Box fullWidth fullPadding align="center" gap={TWENTY_FOUR_PX}>
                      <img src={cards} width={205} />
                      <GreenBox fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey="registrationStep5PaymentSwishPreparingPayment" />
                      </GreenBox>
                    </Box>
                  )}
                  {stripeError.hasError && (
                    <Box fullWidth fullPadding align="center" gap={TWENTY_FOUR_PX}>
                      <img src={cards} width={205} />
                      <RedBox fullWidth fullPadding spacing={EIGHT_PX}>
                        <GrooverText textKey={stripeError.textKey || 'registrationStep5PaymentSwishGenericError'} />
                      </RedBox>
                    </Box>
                  )}
                </PurpleContainer>
              ))}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.Cash) &&
              (state?.paymentMethod !== PaymentMethod.Cash ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.Cash)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.Cash} />
                    <GrooverText textKey="registrationStep5PaymentRegPaymentCash" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.Cash) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Cash))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={iconCompleted} width={20} />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.Cash)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.Cash} />
                      <GrooverText textKey="registrationStep5PaymentRegPaymentCash" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.Cash) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Cash))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={iconCompleted} width={20} />
                    </Box>
                  </HeaderBox>
                  <Box fullWidth top bottom left right>
                    <Box fullWidth direction="row" align="center" justify="space-between" gap={EIGHT_PX}>
                      <Box>
                        <span>
                          {formatGrooverText(getGrooverText('registrationStep5PaymentCashInfo'), [
                            formatToBePaid(payFor as PayForEnum, true)
                          ])}
                        </span>
                      </Box>
                      <ContinueButton onClick={onHandleNext}>
                        <GrooverText textKey="registrationCtactaContinue" />
                        <img src={iconArrow} width={16} height={16} />
                      </ContinueButton>
                    </Box>
                  </Box>
                </PurpleContainer>
              ))}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.Fritidskortet) &&
              (fritidskortetAlreadyUsedForEnrollment && !fritidskortetPolling ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  style={{ opacity: 0.7, cursor: 'not-allowed', pointerEvents: 'none' }}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={false} disabled />
                    <Box direction="column" gap={FOUR_PX}>
                      <GrooverText textKey="registrationStep5PaymentRegPaymentFritidskortet" />
                      <GrooverText
                        textKey="registrationStep5PaymentFritidskortetAlreadyUsed"
                        variant="regular-dark"
                        style={{ fontSize: '12px', color: '#888' }}
                      />
                    </Box>
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.Fritidskortet) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Fritidskortet))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={fritidskortetIcon} width="20px" alt="Fritidskortet" />
                  </Box>
                </PaymentBox>
              ) : state?.paymentMethod !== PaymentMethod.Fritidskortet && !fritidskortetPolling ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.Fritidskortet)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.Fritidskortet} />
                    <GrooverText textKey="registrationStep5PaymentRegPaymentFritidskortet" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.Fritidskortet) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Fritidskortet))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={fritidskortetIcon} width="20px" alt="Fritidskortet" />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.Fritidskortet)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.Fritidskortet} />
                      <GrooverText textKey="registrationStep5PaymentRegPaymentFritidskortet" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.Fritidskortet) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Fritidskortet))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={fritidskortetIcon} width="20px" alt="Fritidskortet" />
                    </Box>
                  </HeaderBox>
                  <Box fullWidth top bottom left right>
                    <InformationContainer fullWidth fullPadding spacing={EIGHT_PX}>
                      <GrooverText textKey="registrationStep5PaymentFritidskortetInstructions" variant="regular-dark" />
                      <Box direction="column" gap={EIGHT_PX} top>
                        {(() => {
                          const fkFee = getPaymentMethodFeeForMethod(PaymentMethod.Fritidskortet)
                          const fkTotal = (toBePaid[payFor as PayForEnum] || 0) + fkFee
                          const { fritidskortetCoversInOre: fkCovers, userPaysInOre: userPays } =
                            calcFritidskortetAmounts(fkTotal)
                          return (
                            <>
                              <Box fullWidth direction="row" align="center" gap={EIGHT_PX}>
                                <GrooverText
                                  textKey="registrationStep5PaymentFritidskortetSubsidyLabel"
                                  variant="regular-dark"
                                />
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    marginLeft: 'auto',
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {formatAmount(fkCovers)} {currency}
                                </span>
                              </Box>
                              <Box fullWidth direction="row" align="center" gap={EIGHT_PX}>
                                <GrooverText
                                  textKey="registrationStep5PaymentFritidskortetUserPaysLabel"
                                  variant="regular-dark"
                                />
                                <span
                                  style={{
                                    fontWeight: 'bold',
                                    marginLeft: 'auto',
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {formatAmount(userPays)} {currency}
                                </span>
                              </Box>
                            </>
                          )
                        })()}
                        <Box fullWidth direction="row" align="center" gap={EIGHT_PX}>
                          <GrooverText
                            textKey="registrationStep5PaymentFritidskortetBgAccountLabel"
                            variant="regular-dark"
                          />
                          <span
                            style={{ fontWeight: 'bold', marginLeft: 'auto', textAlign: 'right', whiteSpace: 'nowrap' }}
                          >
                            {client?.bankGiro || '-'}
                          </span>
                        </Box>
                        <Box fullWidth direction="row" align="center" gap={EIGHT_PX}>
                          <GrooverText
                            textKey="registrationStep5PaymentFritidskortetReferenceLabel"
                            variant="regular-dark"
                          />
                          <span
                            style={{
                              fontWeight: 'bold',
                              fontFamily: 'monospace',
                              marginLeft: 'auto',
                              textAlign: 'right',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {state?.obfuscatedEnrollment?.ocr || enrollment?.ocr || '-'}
                          </span>
                        </Box>
                        <Box fullWidth direction="row" align="center" gap={EIGHT_PX}>
                          <GrooverText
                            textKey="registrationStep5PaymentFritidskortetRegisterLink"
                            variant="regular-dark"
                          />
                          <a
                            href="https://fritidskortet.ehalsomyndigheten.se/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#7c3aed',
                              textDecoration: 'underline',
                              marginLeft: 'auto',
                              textAlign: 'right',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <GrooverText
                              textKey="registrationStep5PaymentFritidskortetRegisterUrlLabel"
                              variant="regular-dark"
                            />
                          </a>
                        </Box>
                      </Box>
                    </InformationContainer>
                    <Box fullWidth direction="column" gap={SIXTEEN_PX} top>
                      <TextField
                        fullWidth
                        size="small"
                        label={getGrooverText('registrationStep5PaymentFritidskortetGuardianSsnLabel')}
                        value={fritidskortetGuardianSsn}
                        onChange={(e) => !fritidskortetPolling && setFritidskortetGuardianSsn(e.target.value)}
                        placeholder="YYYYMMDD-XXXX"
                        error={
                          !!fritidskortetGuardianSsn && !SSN_REGEX.test(fritidskortetGuardianSsn.replace(/\s/g, ''))
                        }
                        helperText={
                          fritidskortetGuardianSsn && !SSN_REGEX.test(fritidskortetGuardianSsn.replace(/\s/g, ''))
                            ? getGrooverText('validationSsnInvalidSweden')
                            : undefined
                        }
                        inputProps={{ maxLength: 13, readOnly: fritidskortetPolling }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label={getGrooverText('registrationStep5PaymentFritidskortetChildSsnLabel')}
                        value={fritidskortetChildSsn}
                        onChange={(e) => !fritidskortetPolling && setFritidskortetChildSsn(e.target.value)}
                        placeholder="YYYYMMDD-XXXX"
                        error={!!fritidskortetChildSsn && !SSN_REGEX.test(fritidskortetChildSsn.replace(/\s/g, ''))}
                        helperText={
                          fritidskortetChildSsn && !SSN_REGEX.test(fritidskortetChildSsn.replace(/\s/g, ''))
                            ? getGrooverText('validationSsnInvalidSweden')
                            : undefined
                        }
                        inputProps={{ maxLength: 13, readOnly: fritidskortetPolling }}
                      />
                      {/* Fritidskortet inline error message */}
                      {fritidskortetError && (
                        <Box
                          fullWidth
                          style={{
                            background: '#fef2f2',
                            border: '1px solid #fca5a5',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            color: '#dc2626',
                            fontSize: '13px'
                          }}
                        >
                          {fritidskortetError}
                        </Box>
                      )}
                      {/* Fritidskortet 4-stage payment stepper */}
                      {(() => {
                        const stage = fritidskortetAlreadyUsedForEnrollment
                          ? 'paid'
                          : fritidskortetSessionId
                            ? 'confirm'
                            : 'submit'
                        const canSubmit =
                          !fritidskortetAlreadyUsedForEnrollment &&
                          !fritidskortetSubmitting &&
                          SSN_REGEX.test(fritidskortetGuardianSsn.replace(/\s/g, '').trim()) &&
                          SSN_REGEX.test(fritidskortetChildSsn.replace(/\s/g, '').trim())

                        const stepStyle = (active: boolean, done: boolean): import('react').CSSProperties => ({
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: active ? '2px solid #7c3aed' : '1px solid #d1d5db',
                          background: done ? '#f3f0ff' : active ? '#7c3aed' : 'transparent',
                          color: active ? '#fff' : done ? '#7c3aed' : '#9ca3af',
                          fontWeight: active || done ? 600 : 400,
                          fontSize: '13px',
                          cursor:
                            active && stage === 'submit'
                              ? 'pointer'
                              : active && stage === 'confirm'
                                ? 'pointer'
                                : 'default',
                          opacity: active || done ? 1 : 0.5,
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        })

                        // While submitting: button looks active (purple) but is not clickable
                        const payButtonActive = stage === 'submit' && (canSubmit || fritidskortetSubmitting)
                        const payButtonDone = stage !== 'submit'

                        return (
                          <Box fullWidth direction="row" align="center" gap={EIGHT_PX} style={{ flexWrap: 'wrap' }}>
                            {/* Stage 1: Pay */}
                            <button
                              style={{
                                ...stepStyle(payButtonActive, payButtonDone),
                                cursor: fritidskortetSubmitting
                                  ? 'default'
                                  : stepStyle(payButtonActive, payButtonDone).cursor
                              }}
                              onClick={
                                stage === 'submit' && canSubmit && !fritidskortetSubmitting
                                  ? handleFritidskortetPay
                                  : undefined
                              }
                              disabled={stage === 'submit' && !canSubmit && !fritidskortetSubmitting}
                            >
                              {fritidskortetSubmitting ? (
                                <CircularProgress size={12} style={{ color: 'inherit' }} />
                              ) : stage === 'submit' && canSubmit ? (
                                <span>→</span>
                              ) : null}
                              <GrooverText
                                textKey="registrationStep5PaymentFritidskortetPayButton"
                                style={{ color: 'inherit', fontSize: '13px' }}
                              />
                            </button>

                            {/* Stage 2: Confirm (open EHM) */}
                            <button
                              style={{
                                ...stepStyle(
                                  stage === 'confirm' && !fritidskortetConfirmClicked,
                                  stage === 'paid' || (stage === 'confirm' && fritidskortetConfirmClicked)
                                ),
                                cursor: stage === 'confirm' ? 'pointer' : 'default'
                              }}
                              onClick={
                                stage === 'confirm'
                                  ? () => {
                                      setFritidskortetConfirmClicked(true)
                                      window.open(
                                        'https://fritidskortet.ehalsomyndigheten.se/',
                                        '_blank',
                                        'noopener,noreferrer'
                                      )
                                    }
                                  : undefined
                              }
                              disabled={stage !== 'confirm' && stage !== 'paid'}
                            >
                              {stage === 'confirm' && !fritidskortetConfirmClicked && <span>→</span>}
                              <GrooverText
                                textKey="registrationStep5PaymentFritidskortetConfirmButton"
                                style={{ color: 'inherit', fontSize: '13px' }}
                              />
                              <OpenInNewIcon style={{ fontSize: '14px', color: 'inherit', verticalAlign: 'middle' }} />
                            </button>

                            {/* Stage 3: Awaiting signing (active once confirm clicked, spinner while polling) */}
                            <div
                              style={stepStyle(stage === 'confirm' && fritidskortetConfirmClicked, stage === 'paid')}
                            >
                              {fritidskortetPolling && stage !== 'paid' ? (
                                <CircularProgress
                                  size={12}
                                  style={{ color: fritidskortetConfirmClicked ? '#fff' : '#7c3aed' }}
                                />
                              ) : null}
                              <GrooverText
                                textKey="registrationStep5PaymentFritidskortetAwaitingLabel"
                                style={{ color: 'inherit', fontSize: '13px' }}
                              />
                            </div>

                            {/* Stage 4: Paid */}
                            <div style={stepStyle(stage === 'paid', false)}>
                              {stage === 'paid' && <span>✓</span>}
                              <GrooverText
                                textKey="registrationStep5PaymentFritidskortetPaidLabel"
                                style={{ color: 'inherit', fontSize: '13px' }}
                              />
                            </div>
                          </Box>
                        )
                      })()}
                    </Box>
                    <Box fullWidth direction="row" align="center" justify="flex-end" top>
                      <ContinueButton
                        onClick={onHandleNext}
                        isDisabled={state?.paymentMethod === PaymentMethod.Fritidskortet && !state.isPaid}
                      >
                        <GrooverText textKey="registrationCtactaContinue" style={{ color: 'inherit' }} />
                        <img src={iconArrow} width={16} height={16} />
                      </ContinueButton>
                    </Box>
                  </Box>
                </PurpleContainer>
              ))}
            {/* ePassi payment method */}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.Epassi) &&
              (state?.paymentMethod !== PaymentMethod.Epassi ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.Epassi)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.Epassi} />
                    <GrooverText textKey="registrationStep5PaymentEpassiTitle" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.Epassi) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Epassi))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={epassiIcon} width="20px" alt="ePassi" />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.Epassi)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.Epassi} />
                      <GrooverText textKey="registrationStep5PaymentEpassiTitle" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.Epassi) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.Epassi))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={epassiIcon} width="20px" alt="ePassi" />
                    </Box>
                  </HeaderBox>
                  <Box fullWidth top bottom left right>
                    {/* ePassi: Waiting for webhook confirmation after return from ePassi */}
                    {epassiWaiting && !state.isPaid ? (
                      <>
                        <InformationContainer fullWidth fullPadding spacing={EIGHT_PX}>
                          <Box direction="row" align="center" gap={EIGHT_PX}>
                            <CircularProgress size={18} />
                            <GrooverText textKey="registrationStep5PaymentEpassiWaiting" variant="regular-dark" />
                          </Box>
                        </InformationContainer>
                        <Box fullWidth direction="row" align="center" justify="flex-end" gap={EIGHT_PX}>
                          <ContinueButton onClick={onHandleNext} isDisabled={!state.isPaid}>
                            <GrooverText textKey="registrationCtactaContinue" style={{ color: 'inherit' }} />
                            <img src={iconArrow} width={16} height={16} />
                          </ContinueButton>
                        </Box>
                      </>
                    ) : state.isPaid ? (
                      <>
                        {/* ePassi: Payment confirmed */}
                        <InformationContainer fullWidth fullPadding spacing={EIGHT_PX}>
                          <Box direction="row" align="center" gap={EIGHT_PX}>
                            <img src={iconCompleted} width={20} height={20} alt="" />
                            <GrooverText textKey="registrationStep5PaymentEpassiPaidLabel" variant="regular-dark" />
                          </Box>
                        </InformationContainer>
                        <Box fullWidth direction="row" align="center" justify="flex-end" gap={EIGHT_PX}>
                          <ContinueButton onClick={onHandleNext}>
                            <GrooverText textKey="registrationCtactaContinue" style={{ color: 'inherit' }} />
                            <img src={iconArrow} width={16} height={16} />
                          </ContinueButton>
                        </Box>
                      </>
                    ) : (
                      <>
                        {/* ePassi: Default initiation state */}
                        <InformationContainer fullWidth fullPadding spacing={EIGHT_PX}>
                          <GrooverText textKey="registrationStep5PaymentEpassiInstructions" variant="regular-dark" />
                          <Box direction="column" gap={EIGHT_PX} top>
                            {(() => {
                              const epassiFee = getPaymentMethodFeeForMethod(PaymentMethod.Epassi)
                              const epassiTotal = (toBePaid[payFor as PayForEnum] || 0) + epassiFee
                              return (
                                <>
                                  <Box fullWidth direction="row" align="center" gap={EIGHT_PX}>
                                    <GrooverText
                                      textKey="registrationStep5PaymentEpassiAmountLabel"
                                      variant="regular-dark"
                                    />
                                    <span
                                      style={{
                                        fontWeight: 'bold',
                                        marginLeft: 'auto',
                                        textAlign: 'right',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {formatAmount(epassiTotal)} {currency}
                                    </span>
                                  </Box>
                                  {epassiFee > 0 && (
                                    <Box fullWidth direction="row" align="center" gap={EIGHT_PX}>
                                      <GrooverText
                                        textKey="registrationStep5PaymentEpassiFeeLabel"
                                        variant="regular-dark"
                                      />
                                      <span
                                        style={{
                                          fontWeight: 'bold',
                                          marginLeft: 'auto',
                                          textAlign: 'right',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        +{formatAmount(epassiFee)} {currency}
                                      </span>
                                    </Box>
                                  )}
                                </>
                              )
                            })()}
                          </Box>
                        </InformationContainer>
                        {/* ePassi inline error message */}
                        {epassiError && (
                          <Box
                            fullWidth
                            style={{
                              background: '#fef2f2',
                              border: '1px solid #fca5a5',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              color: '#dc2626',
                              fontSize: '13px',
                              marginBottom: EIGHT_PX
                            }}
                          >
                            {epassiError}
                          </Box>
                        )}
                        <Box fullWidth direction="row" align="center" justify="space-between" gap={EIGHT_PX}>
                          <button
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 16px',
                              borderRadius: '6px',
                              border: '2px solid #7c3aed',
                              background: epassiSubmitting ? '#9b59b6' : '#7c3aed',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '14px',
                              cursor: epassiSubmitting ? 'default' : 'pointer',
                              opacity: epassiSubmitting ? 0.7 : 1
                            }}
                            onClick={!epassiSubmitting ? handleEpassiPay : undefined}
                            disabled={epassiSubmitting}
                          >
                            {epassiSubmitting ? (
                              <>
                                <CircularProgress size={14} style={{ color: 'inherit' }} />
                                <GrooverText
                                  textKey="registrationStep5PaymentEpassiSubmitting"
                                  style={{ color: 'inherit', fontSize: '14px' }}
                                />
                              </>
                            ) : (
                              <GrooverText
                                textKey="registrationStep5PaymentEpassiPayButton"
                                style={{ color: 'inherit', fontSize: '14px' }}
                              />
                            )}
                          </button>
                          <ContinueButton
                            onClick={onHandleNext}
                            isDisabled={state?.paymentMethod === PaymentMethod.Epassi && !state.isPaid}
                          >
                            <GrooverText textKey="registrationCtactaContinue" style={{ color: 'inherit' }} />
                            <img src={iconArrow} width={16} height={16} />
                          </ContinueButton>
                        </Box>
                      </>
                    )}
                    {/* Hidden form for ePassi POST redirect */}
                    <form ref={epassiFormRef} style={{ display: 'none' }} />
                  </Box>
                </PurpleContainer>
              ))}
            {!isPaymentPriceBlocked &&
              enabledPaymentMethods.some((pm) => pm.method == PaymentMethod.SwishManual) &&
              (state?.paymentMethod !== PaymentMethod.SwishManual ? (
                <PaymentBox
                  fullWidth
                  direction="row"
                  align="center"
                  justify="space-between"
                  right
                  spacing={EIGHT_PX}
                  onClick={() => onSelectPaymentMethod(PaymentMethod.SwishManual)}
                >
                  <Box direction="row" align="center" gap={FOUR_PX}>
                    <Radio checked={(state?.paymentMethod as PaymentMethod) === PaymentMethod.SwishManual} />
                    <GrooverText textKey="registrationStep5PaymentRegPaymentSwishManual" />
                    {client && getPaymentMethodFeeForMethod(PaymentMethod.SwishManual) > 0 && (
                      <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                        +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.SwishManual))} {currency}
                      </span>
                    )}
                  </Box>
                  <Box direction="row" align="center" gap={TWELVE_PX}>
                    <img src={swishIcon} width="20px" />
                  </Box>
                </PaymentBox>
              ) : (
                <PurpleContainer fullWidth>
                  <HeaderBox
                    fullWidth
                    direction="row"
                    align="center"
                    justify="space-between"
                    right
                    spacing={EIGHT_PX}
                    onClick={() => onSelectPaymentMethod(PaymentMethod.SwishManual)}
                  >
                    <Box direction="row" align="center" gap={FOUR_PX}>
                      <Radio checked={state?.paymentMethod === PaymentMethod.SwishManual} />
                      <GrooverText textKey="registrationStep5PaymentRegPaymentSwishManual" />
                      {client && getPaymentMethodFeeForMethod(PaymentMethod.SwishManual) > 0 && (
                        <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                          +{formatAmount(getPaymentMethodFeeForMethod(PaymentMethod.SwishManual))} {currency}
                        </span>
                      )}
                    </Box>
                    <Box direction="row" align="center" gap={TWELVE_PX}>
                      <img src={swishIcon} width="20px" />
                    </Box>
                  </HeaderBox>
                  <Box fullWidth top bottom left right>
                    <Box fullWidth direction="row" align="center" justify="space-between" gap={EIGHT_PX}>
                      <Box>
                        <span>
                          {formatGrooverText(getGrooverText('registrationStep5PaymentSwishManualInfoExtended'), [
                            formatToBePaid(payFor as PayForEnum, true),
                            client?.swishForetagNumber
                          ])}
                        </span>
                      </Box>
                      <ContinueButton onClick={onHandleNext}>
                        <GrooverText textKey="registrationCtactaContinue" />
                        <img src={iconArrow} width={16} height={16} />
                      </ContinueButton>
                    </Box>
                  </Box>
                </PurpleContainer>
              ))}
          </Box>
        </div>
      ) : null}
      {/* /!state.isPaid && (hasConfirmedAmountDue || !hasPendingPositiveAmount) */}

      {/* JSON Modal for localhost development */}
      {isDev && (
        <JsonModal
          open={jsonModalOpen}
          onClose={() => setJsonModalOpen(false)}
          data={jsonModalData}
          title="Obfuscated Enrollment Data"
        />
      )}
    </OpenContainer>
  )
}

const SuccessMessage = styled(Box)`
  background: #8aff7e;
  border-radius: ${BorderRadixes.small};
`

const PaidProcessingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SIXTEEN_PX};
  pointer-events: all;
  cursor: wait;
`

const GrayBox = styled(Box)`
  max-width: 350px;
  background-color: #efefef;
  border-radius: ${BorderRadixes.regular};
`

const PaymentBox = styled(Box)`
  border: 1px solid #e6e6e6;
  margin-top: ${EIGHT_PX};
  border-radius: ${BorderRadixes.small};
  cursor: pointer;
`

const PurpleContainer = styled(Box)`
  margin-top: ${EIGHT_PX};
  border: 1px solid #7617d0;
  box-shadow: 0px 0px 8px 0px #7617d0;
  border-radius: ${BorderRadixes.small};
`

const HeaderBox = styled(Box)`
  border-bottom: 1px solid #c9c9c9;
`

const Header = styled(Box)`
  border-radius: ${BorderRadixes.regular};
  background-color: rgba(106, 171, 55, 0.2);
`

const GreenBox = styled(Box)`
  border-radius: ${BorderRadixes.regular};
  background-color: rgba(106, 171, 55, 0.2);
`

const InformationContainer = styled(Box)`
  margin-top: ${TWELVE_PX};
  margin-bottom: ${THIRTY_TWO_PX};
  border-radius: ${BorderRadixes.regular};
  background-color: rgba(106, 171, 55, 0.1);
`

const RedBox = styled(Box)`
  border-radius: ${BorderRadixes.regular};
  background-color: rgba(171, 55, 55, 0.2);
`

const PartlyApprovedBannerContainer = styled(Box)`
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: ${BorderRadixes.regular};
  padding: 12px 16px;
  margin-bottom: ${SIXTEEN_PX};
`

const PartlyApprovedBannerTitle = styled('div')`
  font-weight: 600;
  color: #b45309;
  margin-bottom: 4px;
`

const PartlyApprovedBannerInfo = styled('div')`
  color: #92400e;
`

const PartlyApprovedPaymentBanner = () => (
  <PartlyApprovedBannerContainer fullWidth>
    <PartlyApprovedBannerTitle>
      <GrooverText textKey="registrationStep4PartlyApprovedBannerTitle" />
    </PartlyApprovedBannerTitle>
    <PartlyApprovedBannerInfo>
      <GrooverText textKey="registrationStep4PartlyApprovedBannerInfo" />
    </PartlyApprovedBannerInfo>
  </PartlyApprovedBannerContainer>
)

export default memo(CourseRegistrationStepFour)
